import { t } from "i18next";
import { BrightMarketApi } from "./BrightMarketApi";
import { StatusResponse } from "./BrightMarketApiType";
import { identifyClarityUser } from "../helpers/ClarityHelper";
import {
  Article,
  Supplier,
  BusinessUnit,
  BranchOffice,
  Category,
  ChannelType,
  Currency,
  Language,
  Organization,
  Privilege,
  Rol,
  RoundingType,
} from "../models/Models";
import { getOrganizationBySubdomain, getOrganizationId } from "./Helper";
import type {
  ProductAnalysisBreakdown,
  ProductAnalysisChart,
  ProductAnalysisDetail,
  ProductAnalysisDetailBody,
  ProductAnalysisHeader,
  ProductAnalysisKpis,
  ProductAnalysisLandingPage,
  ProductSimilarArticles,
} from "../types/productAnalysis";
import type {
  BrandAnalysisDetail,
  BrandAnalysisDetailBreakdown,
  BrandAnalysisDetailChart,
  BrandAnalysisDetailCore,
  BrandAnalysisLanding,
} from "../types/brandAnalysis";
import type {
  StoreAnalysisDetail,
  StoreAnalysisDetailBreakdown,
  StoreAnalysisDetailChart,
  StoreAnalysisDetailCore,
  StoreAnalysisLanding,
} from "../types/storeAnalysis";
import type {
  ChannelAnalysisDetail,
  ChannelAnalysisDetailBreakdown,
  ChannelAnalysisDetailChart,
  ChannelAnalysisDetailCore,
  ChannelAnalysisLanding,
} from "../types/channelAnalysis";
import type {
  CategoryAnalysisDetail,
  CategoryAnalysisDetailBreakdown,
  CategoryAnalysisDetailChart,
  CategoryAnalysisDetailCore,
} from "../types/categoryAnalysis";

export type {
  ChannelAnalysisClientKpis,
  ChannelAnalysisDetail,
  ChannelAnalysisDetailBreakdown,
  ChannelAnalysisDetailChart,
  ChannelAnalysisDetailCore,
  ChannelAnalysisEvolutionPoint,
  ChannelAnalysisGeoRow,
  ChannelAnalysisHeader,
  ChannelAnalysisKpis,
  ChannelAnalysisLanding,
  ChannelAnalysisLandingItem,
  ChannelAnalysisMixRow,
  ChannelAnalysisNewClientsByMonth,
  ChannelAnalysisProductMixRow,
  ChannelAnalysisRankingRow,
} from "../types/channelAnalysis";

export type {
  ProductAnalysisBreakdown,
  ProductAnalysisChannelRow,
  ProductAnalysisChart,
  ProductAnalysisComplementary,
  ProductAnalysisDetail,
  ProductAnalysisDetailBody,
  ProductAnalysisDriverInsight,
  ProductAnalysisEvolutionPoint,
  ProductAnalysisHeader,
  ProductAnalysisKpis,
  ProductAnalysisLandingItem,
  ProductAnalysisLandingPage,
  ProductAnalysisPromotion,
  ProductAnalysisSimilar,
  ProductAnalysisSkuBreakdown,
  ProductAnalysisStoreRow,
} from "../types/productAnalysis";

export type {
  BrandAnalysisComplementaryBrand,
  BrandAnalysisDependencyRow,
  BrandAnalysisDetail,
  BrandAnalysisDetailBreakdown,
  BrandAnalysisDetailChart,
  BrandAnalysisDetailCore,
  BrandAnalysisDriver,
  BrandAnalysisEvolutionPoint,
  BrandAnalysisLanding,
  BrandAnalysisLandingItem,
  BrandAnalysisPerformanceRow,
  BrandAnalysisPotentialBrand,
  BrandAnalysisProductRow,
  BrandAnalysisPromotionRow,
  BrandAnalysisSubstituteBrand,
} from "../types/brandAnalysis";

export const login = async (
  username: string,
  password: string,
  organizationName: string
) => {
  let response;
  try {
    response = await BrightMarketApi.login(
      username,
      password,
      organizationName
    );

    if (response.data.status === StatusResponse.Ok) {
      const token = response.data.data.token;
      const refreshToken = response.data.data.refreshToken;
      localStorage.setItem("token", token);
      if (refreshToken) {
        localStorage.setItem("refreshToken", refreshToken);
      }
      localStorage.setItem("username", username);

      const organization = await BrightMarketApi.fetchOrganizationByName(
        organizationName,
        token
      );
      localStorage.setItem("organizationId", organization.data.id);
      localStorage.setItem("organizationName", organizationName);
      localStorage.setItem("superAdmin", response.data.data.superAdmin);
      localStorage.setItem(
        "privileges",
        JSON.stringify(response.data.data.privileges)
      );
      identifyClarityUser();

      return {
        status: true,
        token: token,
        message: "",
      };
    } else {
      return {
        status: false,
        message: t("Error-IncorrectCredentials"),
      };
    }
  } catch (e) {}
  return {
    status: false,
    message: t("Error-CommonError"),
  };
};

export const loginSuperAdmin = async (organizationName: string) => {
  let response;
  try {
    response = await BrightMarketApi.loginSuperAdmin(organizationName);

    if (response.data.status === StatusResponse.Ok) {
      const token = response.data.data.token;
      const refreshToken = response.data.data.refreshToken;
      localStorage.setItem("token", token);
      if (refreshToken) {
        localStorage.setItem("refreshToken", refreshToken);
      }

      const organization = await BrightMarketApi.fetchOrganizationByName(
        organizationName,
        token
      );
      localStorage.setItem("organizationId", organization.data.id);
      localStorage.setItem("organizationName", organizationName);
      localStorage.setItem("superAdmin", response.data.data.superAdmin);
      localStorage.setItem(
        "privileges",
        JSON.stringify(response.data.data.privileges)
      );
      identifyClarityUser();

      return {
        status: true,
        token: token,
        message: "",
      };
    } else {
      return {
        status: false,
        message: t("Error-IncorrectCredentials"),
      };
    }
  } catch (e) {}
  return {
    status: false,
    message: t("Error-CommonError"),
  };
};

export const getOrganizations = async () => {
  const ret: Organization[] = [];

  try {
    const { organizations } = await BrightMarketApi.getOrganizations();
    organizations?.forEach(function (org: any) {
      let organization: Organization = {
        id: org.id,
        name: org.name,
      };
      ret.push(organization);
    });
  } catch (e) {}
  return ret;
};

export const organizationExists = async (name: string) => {
  try {
    return await BrightMarketApi.organizationExists(name);
  } catch (e) {
    return false;
  }
};

export const searchOrganizations = async (name: string) => {
  const ret: Organization[] = [];

  try {
    const response = await BrightMarketApi.searchOrganizations(name);
    response?.forEach(function (org: any) {
      let organization: Organization = {
        id: org.data.id,
        name: org.data.name,
      };
      ret.push(organization);
    });
  } catch (e) {}
  return ret;
};

export const registerOrganization = async (
  organizationName: string,
  username: string,
  password: string
) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.registerOrganization(
      organizationName,
      username,
      password
    );

    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {}
  return ret;
};

export const validateEmail = async (token: string) => {
  let response: any = {};
  try {
    response = await BrightMarketApi.validateEmail(token);
  } catch (e) {}
  if (response.data.status === StatusResponse.Ok) return response.data.data;
};

export const confirmUserWithToken = async (token: string) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.confirmUser(token);
    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {}
  return ret;
};

export const confirmUserAndSetPassword = async (
  token: string,
  password: string
) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.confirmUserAndSetPassword(
      token,
      password
    );
    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {}
  return ret;
};

export const resendValidateEmail = async (
  email: string,
  organizationName: string
) => {
  let ret = false;

  try {
    let response = await BrightMarketApi.resendValidateEmail(
      email,
      organizationName
    );
    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {}
  return ret;
};

export const sendForgotPassword = async (email: string) => {
  let ret = false;
  const organizationName = await getOrganizationBySubdomain();
  try {
    let response = await BrightMarketApi.sendForgotPassword(
      email,
      organizationName
    );
    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {}
  return ret;
};

export const sendResetPassword = async (password: string, token: string) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.sendResetPassword(password, token);
    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {}
  return ret;
};

export const existsOrganizationByName = async (organizationName: string) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.existsOrganizationByName(
      organizationName
    );
    ret = response.data;
  } catch (e) {}
  return ret;
};

export const getUsers = async (filters: {
  email?: string;
  name?: string;
  businessUnitId?: string;
  rolId?: number;
  branchOfficeId?: number;
}) => {
  const ret: any[] = [];

  try {
    const organizationId = await getOrganizationId();

    const { users } = await BrightMarketApi.getUsers(organizationId, filters);
    users?.forEach(function (item: any) {
      let user: any = {
        id: item.id,
        name: item.name,
        lastName: item.lastname,
        email: item.email,
        roles: item.roles,
        enabled: item.enabled,
        businessUnits: item.businessUnits,
        branchOffices: item.branchOffices,
      };
      ret.push(user);
    });
  } catch (e) {}
  return ret;
};

export const getUser = async () => {
  let ret: any = { success: false, user: null, apiVersion: null };

  try {
    const response = await BrightMarketApi.getUserWithVersion();
    ret.success = true;
    ret.user = response.user;
    ret.apiVersion = response.apiVersion;
  } catch (e) {}
  return ret;
};

export const createUser = async (
  username: string,
  roles: any,
  businessUnitsIds: any,
  branchOfficesIds: any
) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.createUser(
      username,
      roles,
      businessUnitsIds ?? [],
      branchOfficesIds ?? []
    );
    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {}
  return ret;
};

export const deleteUser = async (userId: number) => {
  let ret = false;

  try {
    let response = await BrightMarketApi.deleteUser(userId);
    ret = response.status === 204;
  } catch (e) {}
  return ret;
};

export const fetchOrganizationByName = async () => {
  let ret;
  try {
    const organizationName = await getOrganizationBySubdomain();

    const response = await BrightMarketApi.fetchOrganizationByName(
      organizationName
    );
    ret = response.data;
  } catch (e) {}
  return ret;
};
export const fetchOrganizationById = async () => {
  let ret;
  try {
    const organizationId = await getOrganizationId();

    const response = await BrightMarketApi.fetchOrganizationById(
      organizationId
    );
    ret = response.data.data;
  } catch (e) {}
  return ret;
};

export const updateOrganization = async (
  organizationId: string,
  realName: string,
  businessName: string,
  rut: string,
  address: string
) => {
  let ret = false;

  try {
    let response = await BrightMarketApi.updateOrganization(
      organizationId,
      realName,
      businessName,
      rut,
      address
    );
    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {}
  return ret;
};

export const updateOrganizationClientValidationStats = async (
  organizationId: string,
  payload: {
    enabled: boolean;
    refreshFrequency: "HOURLY" | "DAILY";
    dailyHour: number;
    dailyMinute?: number;
    hourlyMinute?: number;
  }
) => {
  let ret = false;
  try {
    const response = await BrightMarketApi.updateOrganizationClientValidationStats(
      organizationId,
      payload
    );
    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {}
  return ret;
};

export const previewClientContactStorageMigration = async (
  organizationId: string
) => {
  const response = await BrightMarketApi.previewClientContactStorageMigration(
    organizationId
  );
  return response.data?.data ?? response.data;
};

export const migrateClientContactStorage = async (
  organizationId: string,
  confirmation: string
) => {
  const response = await BrightMarketApi.migrateClientContactStorage(
    organizationId,
    confirmation
  );
  return response.data?.data ?? response.data;
};

export const getClientContactStorageMigrationStatus = async (
  organizationId: string
) => {
  const response =
    await BrightMarketApi.getClientContactStorageMigrationStatus(
      organizationId
    );
  return response.data?.data ?? response.data;
};

export const getOrganizationClientValidationStatsSettings = async (organizationId: string) => {
  try {
    const response = await BrightMarketApi.getOrganizationClientValidationStatsSettings(
      organizationId
    );
    return response.data?.data ?? response.data;
  } catch (e) {
    console.log("We have the error", e);
    return null;
  }
};

export const listOrganizationsClientValidationStatsSettings = async (
  page: number,
  size: number,
  q?: string
) => {
  try {
    const response = await BrightMarketApi.listOrganizationsClientValidationStatsSettings(
      page,
      size,
      q
    );
    return response.data?.data ?? response.data;
  } catch (e) {
    console.log("We have the error", e);
    return null;
  }
};

export const recomputeOrganizationClientValidationStats = async (organizationId: string) => {
  try {
    const response = await BrightMarketApi.recomputeOrganizationClientValidationStats(
      organizationId
    );
    return response.data?.data ?? response.data;
  } catch (e) {
    console.log("We have the error", e);
    return null;
  }
};

export const getOrganizationClientValidationStatsStatus = async (organizationId: string) => {
  try {
    const response = await BrightMarketApi.getOrganizationClientValidationStatsStatus(
      organizationId
    );
    return response.data?.data ?? response.data;
  } catch (e) {
    console.log("We have the error", e);
    return null;
  }
};

export const getRoles = async (
  pagination: number,
  filters: { name?: string; privilegeId?: number }
) => {
  const roles: Rol[] = [];
  let totalElements = 0;

  try {
    const organizationId = await getOrganizationId();

    const response = await BrightMarketApi.getRoles(
      organizationId,
      pagination,
      filters
    );

    let items = response.roles;
    totalElements = response.totalElements;

    items.roles.forEach(function (item: any) {
      let rol: Rol = {
        id: item.id,
        name: item.name,
        description: item.description,
        privileges: item.privileges,
      };
      roles.push(rol);
    });
  } catch (e) {}
  return { roles, totalElements };
};

export const createRol = async (
  name: string,
  description: string,
  privileges: any
) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.createRol(
      name,
      description,
      privileges
    );

    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {}
  return ret;
};

export const getPrivileges = async () => {
  const privileges: any[] = [];

  try {
    const response = await BrightMarketApi.getPrivileges();
    let items = response.privileges;
    items?.forEach(function (item: any) {
      let privilege: Privilege = {
        id: item.id,
        name: item.name,
        descriptionEn: item.descriptionEn,
        descriptionEs: item.descriptionEs,
      };
      privileges.push(privilege);
    });
  } catch (e) {}
  return privileges;
};

export const getAllRoles = async () => {
  const roles: any[] = [];
  const organizationId = await getOrganizationId();

  try {
    const response = await BrightMarketApi.getAllRoles(organizationId);
    let items = response.roles;
    items?.forEach(function (item: any) {
      let rol: Rol = {
        id: item.id,
        name: item.name,
        description: item.description,
      };
      roles.push(rol);
    });
  } catch (e) {}
  return roles;
};

export const deleteRol = async (rolId: number) => {
  let ret = false;

  try {
    let response = await BrightMarketApi.deleteRol(rolId);
    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const editRol = async (
  rolId: number,
  name: string,
  description: string,
  privileges: any
) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.editRol(
      rolId,
      name,
      description,
      privileges
    );

    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const getCurrencies = async () => {
  const currencies: any[] = [];

  try {
    const response = await BrightMarketApi.getCurrencies();
    let items = response.currencies;
    items.forEach(function (item: any) {
      let currency: Currency = {
        id: item.id,
        name: item.name,
        shortName: item.shortName,
      };
      currencies.push(currency);
    });
  } catch (e) {
    console.log("We have the error", e);
  }
  return currencies;
};

export const getLanguages = async () => {
  const languages: any[] = [];

  try {
    const response = await BrightMarketApi.getLanguages();
    let items = response.languages;
    items.forEach(function (item: any) {
      let language: Language = {
        id: item.id,
        name: item.name,
        shortName: item.shortName,
      };
      languages.push(language);
    });
  } catch (e) {
    console.log("We have the error", e);
  }
  return languages;
};

export const getChannelsTypes = async () => {
  const types: any[] = [];

  try {
    const response = await BrightMarketApi.getChannelsTypes();
    let items = response.channelTypes;
    items.forEach(function (item: any) {
      let type: ChannelType = {
        id: item.id,
        type: item.type,
        descriptionEs: item.descriptionEs,
        descriptionEn: item.descriptionEn,
        currencies: item.currencies,
      };
      types.push(type);
    });
  } catch (e) {
    console.log("We have the error", e);
  }
  return types;
};

export const getBusinessUnits = async (pagination: number) => {
  const businessUnits: BusinessUnit[] = [];
  let totalElements = 0;

  try {
    const organizationId = await getOrganizationId();

    const response = await BrightMarketApi.getBusinessUnits(
      organizationId,
      pagination
    );

    const items = response.businessUnits?.businessUnits ?? response.businessUnits ?? [];
    totalElements = response.totalElements;

    items.forEach(function (item: any) {
      let unit: BusinessUnit = {
        id: item.id,
        name: item.name,
        language: item.language,
        currencies: item.currencies ?? [],
        channels: item.channels,
        targetMargin: item.targetMargin,
      };
      businessUnits.push(unit);
    });
  } catch (e) {
    console.log("We have the error", e);
  }
  return { businessUnits, totalElements };
};

export const getAllBusinessUnits = async () => {
  let businessUnits: any[] = [];

  try {
    const response = await BrightMarketApi.getAllBusinessUnits();
    const items = response.data;
    businessUnits = items;
  } catch (e) {
    console.log("We have the error", e);
  }
  return businessUnits;
};

export const createBusinessUnit = async (
  name: string,
  languageId: number,
  currencyIds: number[]
) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.createBusinessUnit(
      name,
      languageId,
      currencyIds
    );

    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const createChannel = async (
  businessUnitId: number,
  currenciesIds: any,
  channelTypeId: number
) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.createChannel(
      businessUnitId,
      currenciesIds,
      channelTypeId
    );

    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const getAllChannelsByBusinessUnits = async (businessUnitsIds: any) => {
  let channels: any[] = [];
  try {
    const organizationId = await getOrganizationId();
    const items = await BrightMarketApi.getAllChannelsByBusinessUnits(
      organizationId,
      businessUnitsIds
    );
    channels = items.channels;
  } catch (e) {
    console.log("We have the error", e);
  }
  return channels;
};

export const getChannels = async (pagination: number) => {
  let channels: any[] = [];
  let totalElements = 0;

  try {
    const organizationId = await getOrganizationId();

    const response = await BrightMarketApi.getChannels(
      organizationId,
      pagination
    );

    channels = response.channels.channels;
    totalElements = response.totalElements;
  } catch (e) {
    console.log("We have the error", e);
  }
  return { channels, totalElements };
};
export const getAllChannels = async () => {
  let channels: any[] = [];

  try {
    const response = await BrightMarketApi.getAllChannels();
    channels = response.data;
  } catch (e) {
    console.log("We have the error", e);
  }
  return channels;
};

export const deleteChannel = async (channelId: number) => {
  let ret = false;

  try {
    let response = await BrightMarketApi.deleteChannel(channelId);
    ret = response.status === 204;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const deleteBusinessUnit = async (businessUnitId: number) => {
  let ret = false;

  try {
    let response = await BrightMarketApi.deleteBusinessUnit(businessUnitId);
    ret = response.status === 204;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const editBusinessUnit = async (
  businessUnitId: number,
  businessUnit: {
    name?: string;
    languageId?: string;
    currencyIds?: number[];
    targetMargin?: number;
  }
) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.editBusinessUnit(
      businessUnitId,
      businessUnit
    );

    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const editChannel = async (
  channelId: number,
  type: string,
  currenciesIds: any
) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.editChannel(
      channelId,
      type,
      currenciesIds
    );

    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const editUser = async (
  userId: number,
  name: string,
  lastName: string,
  roles: any,
  businessUnitsIds: any,
  branchOfficesIds: any
) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.editUser(
      userId,
      name,
      lastName,
      roles,
      businessUnitsIds ?? [],
      branchOfficesIds ?? []
    );

    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const editUserPassword = async (
  oldPassword: string,
  newPassword: string
) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.editUserPassword(
      oldPassword,
      newPassword
    );

    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const editUserProfile = async (name: string, lastName: string) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.editUserProfile(name, lastName);

    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const getCategoriesByOrganization = async (pagination: number) => {
  const categories: Category[] = [];
  let totalElements = 0;

  try {
    const organizationId = await getOrganizationId();

    const response = await BrightMarketApi.getCategoriesByOrganization(
      organizationId,
      pagination
    );

    const items = response.categories;
    totalElements = response.totalElements;

    items.categories.forEach(function (item: any) {
      let category: Category = {
        id: item.id,
        name: item.name,
        description: item.description,
        father: item.father,
        roundingType: item.roundingType,
        enable: item.enable,
      };
      categories.push(category);
    });
  } catch (e) {
    console.log("We have the error", e);
  }
  return { categories, totalElements };
};

export const getCategoriesFiltered = async (name: string) => {
  let categories = [];

  try {
    const response = await BrightMarketApi.getCategoriesFiltered(name);
    categories = response.data.content;
  } catch (e) {
    console.log("We have the error", e);
  }
  return categories;
};

export const getSuppliersFiltered = async (
  pagination: number,
  filters: {
    name?: string;
    businessUnitId?: string;
    articleId?: string;
    externalId?: string;
  }
) => {
  let suppliers = [];
  let totalElements = 0;

  try {
    const response = await BrightMarketApi.getSuppliersFiltered(
      pagination,
      filters
    );
    suppliers = response.suppliers;
    totalElements = response.totalElements;
  } catch (e) {
    console.log("We have the error", e);
  }
  return { suppliers, totalElements };
};

export const getBranchOfficesFiltered = async (
  pagination: number,
  filters: {
    name?: string;
    externalId?: string;
    businessUnitId?: string;
  }
) => {
  let branchOffices = [];
  let totalElements = 0;

  try {
    const response = await BrightMarketApi.getBranchOfficesFiltered(
      pagination,
      filters
    );
    branchOffices = response.branchoffices;
    totalElements = response.totalElements;
  } catch (e) {
  }
  return { branchOffices, totalElements };
};

export const getClientSegmentsFiltered = async (
  pagination: number,
  filters: {
    name?: string;
    description?: string;
    minMembers?: number;
    maxMembers?: number;
    status?: boolean;
    origin?: string;
    startDate?: string;
    endDate?: string;
    clusterId?: string;
    typeInt?: number;
  }
) => {
  let clientSegments = [];
  let totalElements = 0;

  try {
    const response = await BrightMarketApi.getClientSegmentsFiltered(
      pagination,
      filters
    );
    clientSegments = response.data.content;
    totalElements = response.data.totalElements;
  } catch (e) {
  }
  return { clientSegments, totalElements };
};

export const getClientSegmentsFilteredLite = async (
  pagination: number,
  filters: {
    name?: string;
    description?: string;
    minMembers?: number;
    maxMembers?: number;
    status?: boolean;
    origin?: string;
    startDate?: string;
    endDate?: string;
    clusterId?: string;
    typeInt?: number;
  }
) => {
  let clientSegments = [];
  let totalElements = 0;

  try {
    const response = await BrightMarketApi.getClientSegmentsFilteredLite(
      pagination,
      filters
    );
    clientSegments = response.data.content;
    totalElements = response.data.totalElements;
  } catch (e) {
  }
  return { clientSegments, totalElements };
};

export const getClientSegmentsFilteredWithCount = async (
  pagination: number,
  filters: {
    name?: string;
    description?: string;
    minMembers?: number;
    maxMembers?: number;
    status?: boolean;
    origin?: string;
    startDate?: string;
    endDate?: string;
    clusterId?: string;
    typeInt?: number;
  }
) => {
  let clientSegments = [];
  let totalElements = 0;

  try {
    const response = await BrightMarketApi.getClientSegmentsFilteredWithCount(
      pagination,
      filters
    );
    clientSegments = response.data.content;
    totalElements = response.data.totalElements;
  } catch (e) {
  }
  return { clientSegments, totalElements };
};

export const getClientSegmentsCount = async (ids: number[]) => {
  let counts = [];

  try {
    const response = await BrightMarketApi.getClientSegmentsCount(ids);
    counts = response;
  } catch (e) {
  }
  return counts;
};

export const getSegmentLinkedSuggestions = async (
  segmentId: number | string
) => {
  try {
    const response = await BrightMarketApi.getSegmentLinkedSuggestions(segmentId);
    return response?.data ?? { rewards: [], activationActions: [] };
  } catch (e) {
    return { rewards: [], activationActions: [] };
  }
};

export const getSegmentClusterComposition = async (
  segmentId: number | string
) => {
  let composition: any[] = [];

  try {
    const response = await BrightMarketApi.getSegmentClusterComposition(
      segmentId
    );
    composition = response?.data ?? [];
  } catch (e) {
  }
  return composition;
};

export const getBrandsFiltered = async (name: string) => {
  let brands = [];

  try {
    const response = await BrightMarketApi.getBrandsFiltered(name);
    brands = response.data.content;
  } catch (e) {
    console.log("We have the error", e);
  }
  return brands;
};

export const queryArticlesByName = async (name: string) => {
  let articles = [];

  try {
    const response = await BrightMarketApi.queryArticlesByName(name);
    articles = response.data.content;
  } catch (e) {
    console.log("We have the error", e);
  }
  return articles;
};

export const getPurchaseUnitsFiltered = async (name: string) => {
  let purchaseUnits = [];

  try {
    const response = await BrightMarketApi.getPurchaseUnitsFiltered(name);
    purchaseUnits = response.data.content;
  } catch (e) {
    console.log("We have the error", e);
  }
  return purchaseUnits;
};

export const getDepositsFiltered = async (
  pagination: number,
  filters: { name?: string }
) => {
  let deposits = [];
  let totalElements = 0;

  try {
    const response = await BrightMarketApi.getDepositsFiltered(
      pagination,
      filters
    );
    deposits = response.deposits;
    totalElements = response.totalElements;
  } catch (e) {
    console.log("We have the error", e);
  }
  return { deposits, totalElements };
};

export const getCategoriesByFather = async (
  pagination: number,
  filters: { name?: string; externalId?: string; enable?: number },
  fatherId?: number
) => {
  let categories: any[] = [];
  let totalElements = 0;

  try {
    const organizationId = await getOrganizationId();

    const response = await BrightMarketApi.getCategoriesByFather(
      organizationId,
      pagination,
      filters,
      fatherId
    );

    const items = response.categories;
    totalElements = response.totalElements;

    categories = items.categories;
  } catch (e) {
    console.log("We have the error", e);
  }
  return { categories, totalElements };
};

export const getRoundingTypes = async () => {
  const types: any[] = [];

  try {
    const response = await BrightMarketApi.getRoundingTypes();
    let items = response.roundingTypes;
    items.forEach(function (item: any) {
      let type: RoundingType = {
        id: item.id,
        type: item.type,
        descriptionEs: item.descriptionEs,
        descriptionEn: item.descriptionEn,
      };
      types.push(type);
    });
  } catch (e) {
    console.log("We have the error", e);
  }
  return types;
};

export const getCategoryById = async (categoryId: string) => {
  let ret;
  try {
    const response = await BrightMarketApi.getCategoryById(categoryId);
    ret = response.data;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const createCategory = async (category: {
  name: string;
  externalId: string;
  description: string;
  fatherId: number;
  roundingTypeId: number;
  enable: boolean;
  businessUnitId: any;
}) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.createCategory(category);

    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const deleteCategory = async (categoryId: number) => {
  let ret = false;

  try {
    let response = await BrightMarketApi.deleteCategory(categoryId);
    ret = response.status === 204;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const editCategory = async (
  categoryId: number,
  name: string,
  externalId: string,
  description: string,
  fatherId: number,
  roundingTypeId: number,
  enable: boolean,
  businessUnitId: any
) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.editCategory(
      categoryId,
      name,
      externalId,
      description,
      fatherId ?? "",
      roundingTypeId,
      enable,
      businessUnitId
    );

    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const getAllCategories = async () => {
  const categories: Category[] = [];

  try {
    const response = await BrightMarketApi.getAllCategories();
    const items = response.data;
    items.forEach(function (item: any) {
      let category: Category = {
        id: item.id,
        name: item.name,
        description: item.description,
        father: item.father,
        roundingType: item.roundingType,
        enable: item.enable,
      };
      categories.push(category);
    });
  } catch (e) {
    console.log("We have the error", e);
  }
  return categories;
};

export const getCategoriesPossibleFathers = async (categoryId: number) => {
  const categories: Category[] = [];

  try {
    const items = await BrightMarketApi.getCategoriesPossibleFathers(
      categoryId
    );
    items.forEach(function (item: any) {
      let category: Category = {
        id: item.id,
        name: item.name,
        description: item.description,
        father: item.father,
        roundingType: item.roundingType,
        enable: item.enable,
      };
      categories.push(category);
    });
  } catch (e) {
    console.log("We have the error", e);
  }
  return categories;
};

export const getArticlesByName = async (
  name: string,
  organizationId?: string
) => {
  let totalElements = 0;
  let articles = [];

  try {
    const orgId = organizationId || (await getOrganizationId());

    const response = await BrightMarketApi.getArticlesByName(name, orgId);

    articles = response.articles;
    totalElements = response.totalElements;
  } catch (e) {
    console.log("We have the error", e);
  }
  return { articles, totalElements };
};

export const getArticlesByOrganization = async (pagination: number) => {
  const articles: Article[] = [];
  let totalElements = 0;

  try {
    const organizationId = await getOrganizationId();

    const response = await BrightMarketApi.getArticlesByOrganization(
      organizationId,
      pagination
    );

    const items = response.articles;
    totalElements = response.totalElements;

    items.articles.forEach(function (item: any) {
      let article: Article = {
        id: item.id,
        name: item.name,
        externalId: item.externalId,
        description: item.description,
        category: item.category,
        brand: item.brand,
        cost: item.cost,
        actualPrice: item.actualPrice,
        defaultPrice: item.defaultPrice,
        currency: item.currency,
        roundingType: item.roundingType,
        enable: item.enable,
        skus: item.skus,
        attributes: item.attributes,
        suppliers: item.suppliers,
      };
      articles.push(article);
    });
  } catch (e) {
    console.log("We have the error", e);
  }
  return { articles, totalElements };
};

export const getArticlesFiltered = async (
  pagination: number,
  filters: {
    externalId?: string;
    id?: string;
    categoryId?: string;
    brandId?: number;
    articleName?: string;
    businessUnitId?: string;
  },
  onlyNameId?: boolean,
  projection?: string
) => {
  const articles: Article[] = [];
  let totalElements = 0;

  try {
    const organizationId = await getOrganizationId();

    const response = await BrightMarketApi.getArticlesFiltered(
      organizationId,
      pagination,
      filters,
      onlyNameId,
      projection
    );

    const items = response.articles;
    totalElements = response.totalElements;

    items.articles.forEach(function (item: any) {
      const article: Article = {
        id: item.id,
        name: item.name,
        externalId: item.externalId,
        description: item.description,
        category: item.category,
        brand: item.brand,
        cost: item.cost,
        actualPrice: item.actualPrice,
        defaultPrice: item.defaultPrice,
        currency: item.currency,
        roundingType: item.roundingType,
        enable: item.enable,
        skus: item.skus,
        attributes: item.attributes,
        suppliers: item.suppliers,
      };
      articles.push(article);
    });
  } catch (e) {
    console.log("We have the error", e);
  }
  return { articles, totalElements };
};

export const getSkusFiltered = async (
  pagination: number,
  filters: {
    externalId?: string;
    id?: string;
    categoryId?: string;
    articleId?: string;
    articleName?: string;
    skuName?: string;
  }
) => {
  let skus: any[] = [];
  let totalElements = 0;

  try {
    const organizationId = await getOrganizationId();

    const response = await BrightMarketApi.getSkusFiltered(
      organizationId,
      pagination,
      filters
    );

    skus = response.skus;
    totalElements = response.totalElements;
  } catch (e) {
    console.log("We have the error", e);
  }
  return { skus, totalElements };
};

export const getStockBreaks = async (
  pagination: number,
  filters: {
    articleId?: string;
    branchOfficeId?: string;
    minStock?: string;
    maxStock?: string;
  }
) => {
  let data: any[] = [];
  let totalElements = 0;

  try {
    const response = await BrightMarketApi.getStockBreaks(pagination, filters);
    data = response.data;
    totalElements = response.totalElements;
  } catch (e) {
    console.log("We have the error", e);
  }
  return { data, totalElements };
};

export const getStockAdjustments = async (
  stockId: string,
  type: "deposits" | "branchOffices",
  pagination: number
) => {
  let data: any[] = [];
  let totalElements = 0;

  try {
    const response = await BrightMarketApi.getStockAdjustments(
      stockId,
      type,
      pagination
    );
    data = response.data;
    totalElements = response.totalElements;
  } catch (e) {
    console.log("We have the error", e);
  }
  return { data, totalElements };
};

export const getArticlesWithStock = async (
  pagination: number,
  order = "asc",
  articlesId?: string | null,
  categoriesId?: string | null
) => {
  let articles: any[] = [];
  let totalElements = 0;

  try {
    const response = await BrightMarketApi.getArticlesWithStock(
      pagination,
      order,
      articlesId,
      categoriesId
    );

    const items = response.articles;
    totalElements = response.totalElements;
    articles = items;
  } catch (e) {
    console.log("We have the error", e);
  }

  return { articles, totalElements };
};

export const getArticlesAndSkuWithStock = async (
  pagination: number,
  order = "asc"
) => {
  const articles: any[] = [];
  let totalElements = 0;

  try {
    const response = await BrightMarketApi.getArticlesAndSkuWithStock(
      pagination,
      order
    );

    const items = response.articles;
    totalElements = response.totalElements;
    items.forEach(function (item: any) {
      let article = {
        id: item.id,
        name: item.name,
        suppliers: item.suppliers,
        totalStockArticles: item.totalStockArticles,
        skus: item.skus,
        quantityInPurchaseOrder: item.quantityInPurchaseOrder,
      };
      articles.push(article);
    });
  } catch (e) {
    console.log("We have the error", e);
  }

  return { articles, totalElements };
};

export const getSkusByBranchOfficeId = async (
  branchOfficeId: number,
  pagination: number
) => {
  let totalElements = 0;
  let skus = [];

  try {
    const response = await BrightMarketApi.getSkusByBranchOfficeId(
      branchOfficeId,
      pagination
    );

    skus = response.skus;
    totalElements = response.totalElements;
  } catch (e) {
    console.log("We have the error", e);
  }
  return { skus, totalElements };
};

export const getBrandsByBranchOfficeId = async (
  entity: {
    branchOfficeId?: number;
    depositId?: number;
  },
  pagination: number
) => {
  let totalElements = 0;
  let brands = [];

  try {
    const response = await BrightMarketApi.getBrandsByBranchOfficeId(
      entity,
      pagination
    );

    brands = response.brands;
    totalElements = response.totalElements;
  } catch (e) {
    console.log("We have the error", e);
  }
  return { brands, totalElements };
};

export const getBrandsCatalogue = async (pagination: number) => {
  let totalElements = 0;
  let brands = [];

  try {
    const response = await BrightMarketApi.getBrandsCatalogue(pagination);

    brands = response.brands;
    totalElements = response.totalElements;
  } catch (e) {
    console.log("We have the error", e);
  }
  return { brands, totalElements };
};

export const getCategoriesByBrandCatalogue = async (
  brandId: number,
  pagination: number
) => {
  let totalElements = 0;
  let categories = [];

  try {
    const response = await BrightMarketApi.getCategoriesByBrandCatalogue(
      brandId,
      pagination
    );

    categories = response.categories;
    totalElements = response.totalElements;
  } catch (e) {
    console.log("We have the error", e);
  }
  return { categories, totalElements };
};

export const getSkusByBrandCategoryCatalogue = async (
  brandId: number,
  categoryId: number,
  pagination: number,
  unsupply?: number
) => {
  let totalElements = 0;
  let skus = [];

  try {
    const response = await BrightMarketApi.getSkusByBrandCategoryCatalogue(
      brandId,
      categoryId,
      pagination,
      unsupply
    );

    skus = response.skus;
    totalElements = response.totalElements;
  } catch (e) {
    console.log("We have the error", e);
  }
  return { skus, totalElements };
};

export const getArticlesByBrandCategoryCatalogue = async (
  brandId: number,
  categoryId: number,
  pagination: number,
  unsupply?: number
) => {
  let totalElements = 0;
  let articles = [];

  try {
    const response = await BrightMarketApi.getArticlesByBrandCategoryCatalogue(
      brandId,
      categoryId,
      pagination,
      unsupply
    );

    articles = response.articles;
    totalElements = response.totalElements;
  } catch (e) {
    console.log("We have the error", e);
  }
  return { articles, totalElements };
};

export const getArticlesByBranchOfficeOrDepositAndBrandCategoryCatalogue =
  async (
    entity: {
      branchOfficeId?: number;
      depositId?: number;
    },
    brandId: number,
    categoryId: number,
    pagination: number,
    unsupply?: number
  ) => {
    let totalElements = 0;
    let articles = [];

    try {
      const response =
        await BrightMarketApi.getArticlesByBranchOfficeOrDepositAndBrandCategoryCatalogue(
          entity,
          brandId,
          categoryId,
          pagination,
          unsupply
        );

      articles = response.articles;
      totalElements = response.totalElements;
    } catch (e) {
      console.log("We have the error", e);
    }
    return { articles, totalElements };
  };

export const getCategoriesByBrandAndBranchOfficeId = async (
  entity: {
    branchOfficeId?: number;
    depositId?: number;
  },
  brandId: number,
  pagination: number
) => {
  let totalElements = 0;
  let categories = [];

  try {
    const response =
      await BrightMarketApi.getCategoriesByBrandAndBranchOfficeId(
        entity,
        brandId,
        pagination
      );

    categories = response.categories;
    totalElements = response.totalElements;
  } catch (e) {
    console.log("We have the error", e);
  }
  return { categories, totalElements };
};

export const getSkusByBrandCategoryAndBranchOfficeId = async (
  entity: {
    branchOfficeId?: number;
    depositId?: number;
  },
  brandId: number,
  categoryId: number,
  pagination: number,
  unsupply?: number
) => {
  let totalElements = 0;
  let skus = [];

  try {
    const response =
      await BrightMarketApi.getSkusByBrandCategoryAndBranchOfficeId(
        entity,
        brandId,
        categoryId,
        pagination,
        unsupply
      );

    skus = response.skus;
    totalElements = response.totalElements;
  } catch (e) {
    console.log("We have the error", e);
  }
  return { skus, totalElements };
};

export const getRepositionOrderRequests = async (
  pagination: number,
  branchOfficeDestinyId?: number
) => {
  let totalElements = 0;
  let data = [];

  try {
    const response = await BrightMarketApi.getRepositionOrderRequests(
      pagination,
      branchOfficeDestinyId
    );

    data = response.data;
    totalElements = response.totalElements;
  } catch (e) {
    console.log("We have the error", e);
  }

  return { data, totalElements };
};

export const getSkusWithStockFiltered = async (request: {
  pagination: number;
  articleId?: number;
  skuId?: number;
  branchOfficeId: number;
  businessUnitId: string;
}) => {
  let totalElements = 0;
  let skus = [];

  try {
    const response = await BrightMarketApi.getSkusWithStockFiltered(request);
    skus = response.skus;
    totalElements = response.totalElements;
  } catch (e) {
    console.log("We have the error", e);
  }
  return { skus, totalElements };
};

export const getBranchOfficesByName = async (name: string) => {
  let totalElements = 0;
  let branchOffices = [];

  try {
    const organizationId = await getOrganizationId();

    const response = await BrightMarketApi.getBranchOfficesByName(
      name,
      organizationId
    );

    branchOffices = response.branchOffices;
    totalElements = response.totalElements;
  } catch (e) {
    console.log("We have the error", e);
  }
  return { branchOffices, totalElements };
};

export const getBranchOfficesByNameFast = async (
  name: string,
  offset = 0,
  size = 20
) => {
  let branchOffices = [];

  try {
    const organizationId = await getOrganizationId();

    const response = await BrightMarketApi.getBranchOfficesByNameFast(
      name,
      organizationId,
      offset,
      size
    );

    branchOffices = response.data || [];
  } catch (e) {
    console.log("We have the error", e);
  }
  return { branchOffices };
};

export const getBranchOfficesByNameAndBU = async (
  name: string,
  businessUnitId: string
) => {
  let totalElements = 0;
  let branchOffices = [];

  try {
    const organizationId = await getOrganizationId();

    const response = await BrightMarketApi.getBranchOfficesByNameAndBU(
      name,
      organizationId,
      businessUnitId
    );
    branchOffices = response.branchOffices;
  } catch (e) {
    console.log("We have the error", e);
  }
  return { branchOffices, totalElements };
};

export const getBusinessUnitsByName = async (name: string) => {
  let totalElements = 0;
  let businessUnits = [];

  try {
    const organizationId = await getOrganizationId();

    const response = await BrightMarketApi.getBusinessUnitsByName(
      name,
      organizationId
    );

    businessUnits = response.businessUnits;
    totalElements = response.totalElements;
  } catch (e) {
    console.log("We have the error", e);
  }
  return { businessUnits, totalElements };
};

export const getAllArticles = async () => {
  const articles: any[] = [];

  try {
    const response = await BrightMarketApi.getAllArticles();
    const items = response.data;
    items.forEach(function (item: any) {
      let article = {
        id: item.id,
        name: item.name,
      };
      articles.push(article);
    });
  } catch (e) {
    console.log("We have the error", e);
  }
  return articles;
};

export const createArticle = async (
  article: any,
  suppliers?: any,
  attributes?: any,
  skus?: any
) => {
  let ret: { success: boolean; article: any } = {
    success: false,
    article: null,
  };
  try {
    let response = await BrightMarketApi.createArticle(
      article,
      suppliers,
      attributes,
      skus ?? []
    );

    ret.success = response.data.status === StatusResponse.Ok;
    ret.article = response.data.data;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const deleteArticle = async (articleId: number) => {
  let ret = false;

  try {
    let response = await BrightMarketApi.deleteArticle(articleId);
    ret = response.status === 204;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const getSuppliersByOrganization = async (pagination: number) => {
  const suppliers: Supplier[] = [];
  let totalElements = 0;

  try {
    const organizationId = await getOrganizationId();

    const response = await BrightMarketApi.getSuppliersByOrganization(
      organizationId,
      pagination
    );

    const items = response.suppliers;
    totalElements = response.totalElements;

    items.suppliers.forEach(function (item: any) {
      let supplier: Supplier = {
        id: item.id,
        name: item.name,
        externalId: item.externalId,
        website: item.website,
        catalogueLink: item.catalogueLink,
        articles: item.articles,
        contacts: item.contacts,
      };
      suppliers.push(supplier);
    });
  } catch (e) {
    console.log("We have the error", e);
  }
  return { suppliers, totalElements };
};

export const deleteSupplier = async (supplierId: number) => {
  let ret = false;

  try {
    let response = await BrightMarketApi.deleteSupplier(supplierId);
    ret = response.status === 204;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const createSupplier = async (
  supplier: any,
  seasonFileSeasonIds?: number[] | null,
  seasonFiles?: File[] | null
) => {
  const ret: { success: boolean; supplier: any } = {
    success: false,
    supplier: null,
  };
  try {
    let response = await BrightMarketApi.createSupplierMultipart(
      supplier,
      seasonFileSeasonIds ?? undefined,
      seasonFiles ?? undefined
    );

    ret.success = response.data.status === StatusResponse.Ok;
    ret.supplier = response.data.data;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const getArticleById = async (articleId: string) => {
  let ret;
  try {
    const response = await BrightMarketApi.getArticleById(articleId);
    ret = response.data;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const getSupplierById = async (supplierId: string) => {
  let ret;
  try {
    const response = await BrightMarketApi.getSupplierById(supplierId);
    ret = response.data;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const getAllSuppliers = async () => {
  const suppliers: any[] = [];

  try {
    const response = await BrightMarketApi.getAllSuppliers();
    const items = response.data;
    items.forEach(function (item: any) {
      suppliers.push(item);
    });
  } catch (e) {
    console.log("We have the error", e);
  }
  return suppliers;
};

export const addArticleToSupplier = async (
  articleId: number,
  principal: string,
  supplierId: number,
  purchaseUnitId: number,
  commercialTerm?: any
) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.addArticleToSupplier(
      articleId,
      principal,
      supplierId,
      purchaseUnitId,
      commercialTerm
    );

    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const addContactToSupplier = async (
  supplierId: number,
  name: string,
  role: string,
  phoneNumber: string,
  email: string
) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.addContactToSupplier(
      supplierId,
      name,
      role,
      phoneNumber,
      email
    );
    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const getAllAttributes = async () => {
  const attributes: any[] = [];

  try {
    const response = await BrightMarketApi.getAllAttributes();
    const items = response.data;
    items.forEach(function (item: any) {
      attributes.push(item);
    });
  } catch (e) {
    console.log("We have the error", e);
  }
  return attributes;
};

export const getAttributesFiltered = async (name: string) => {
  const attributes: any[] = [];

  try {
    const response = await BrightMarketApi.getAttributesFiltered(name);
    const items = response.data.content;
    items.forEach(function (item: any) {
      attributes.push(item);
    });
  } catch (e) {
    console.log("We have the error", e);
  }
  return attributes;
};

export const getAttributes = async (
  pagination: number,
  filters: { attribute?: string; value?: string; businessUnitId?: string }
) => {
  const attributes: any[] = [];
  let totalElements = 0;

  try {
    const organizationId = await getOrganizationId();

    const response = await BrightMarketApi.getAttributes(
      organizationId,
      pagination,
      filters
    );

    let items = response.attributes;
    totalElements = response.totalElements;

    items.attributes.forEach(function (attribute: any) {
      attributes.push(attribute);
    });
  } catch (e) {
    console.log("We have the error", e);
  }
  return { attributes, totalElements };
};

export const deleteAttribute = async (attributeId: number) => {
  let ret = false;

  try {
    let response = await BrightMarketApi.deleteAttribute(attributeId);
    ret = response.status === 204;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const createAttribute = async (
  name: string,
  businessUnitsIds: any,
  enums: any
) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.createAttribute(
      name,
      businessUnitsIds ?? [],
      enums ?? []
    );

    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const editAttribute = async (
  attributeId: number,
  name: string,
  businessUnitsIds: any,
  enums: any
) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.editAttribute(
      attributeId,
      name,
      businessUnitsIds ?? [],
      enums ?? []
    );

    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const editSupplier = async (
  supplierId: string,
  supplier: any,
  seasonFileSeasonIds?: number[] | null,
  seasonFiles?: File[] | null
) => {
  let ret = false;

  try {
    let response = await BrightMarketApi.editSupplierMultipart(
      supplierId,
      supplier,
      seasonFileSeasonIds ?? undefined,
      seasonFiles ?? undefined
    );
    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const deleteSupplierSeasonCatalogueFile = async (
  supplierId: string,
  seasonId: number
) => {
  let ok = false;
  try {
    const response = await BrightMarketApi.deleteSupplierSeasonCatalogueFile(
      supplierId,
      seasonId
    );
    ok = response.status === 204;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ok;
};

export const editArticle = async (article: {
  articleId: string;
  name: string;
  externalId: string;
  description: string;
  webDescription: string;
  originType: string;
  categoryId: number;
  brandId: number;
  currencyId: number;
  costCurrency: number;
  roundingTypeId: number;
  cost: number;
  defaultPrice: number;
  minimumPrice: number;
  maximumPrice: number;
  minimumMargin: number;
  enable: boolean;
  discontinued: boolean;
  businessUnitsIds: any;
}) => {
  let ret = false;

  try {
    let response = await BrightMarketApi.editArticle(article);
    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const addAttributeToArticle = async (
  articleId: number,
  attributeId: number,
  enumId: number
) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.addAttributeToArticle(
      articleId,
      attributeId,
      enumId
    );

    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const addSeasonToArticle = async (
  articleId: number,
  seasonId: number
) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.addSeasonToArticle(
      articleId,
      seasonId
    );

    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const addPriceToArticle = async (
  articleId: number,
  branchOfficeId: number,
  price: string,
  date: any
) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.addPriceToArticle(
      articleId,
      branchOfficeId,
      price,
      date
    );

    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const addAttributeToSku = async (
  skuId: number,
  attributeId: number,
  enumId: number
) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.addAttributeToSku(
      skuId,
      attributeId,
      enumId
    );

    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const deleteAttributeFromArticle = async (
  articleAttributeId: number
) => {
  let ret = false;

  try {
    let response = await BrightMarketApi.deleteAttributeFromArticle(
      articleAttributeId
    );
    ret = response.status === 204;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const deleteSeasonFromArticle = async (articleSeasonId: number) => {
  let ret = false;

  try {
    let response = await BrightMarketApi.deleteSeasonFromArticle(
      articleSeasonId
    );
    ret = response.status === 204;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const deleteSkuAttributeFromArticle = async (skuAttributeId: number) => {
  let ret = false;

  try {
    let response = await BrightMarketApi.deleteSkuAttributeFromArticle(
      skuAttributeId
    );
    ret = response.status === 204;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const editAttributeFromArticle = async (
  articleAttributeId: number,
  value: string
) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.editAttributeFromArticle(
      articleAttributeId,
      value
    );

    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const editSkuAttributeFromArticle = async (
  articleSkuAttributeId: number,
  value: string
) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.editSkuAttributeFromArticle(
      articleSkuAttributeId,
      value
    );

    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const deleteContact = async (contactId: number) => {
  let ret = false;

  try {
    let response = await BrightMarketApi.deleteContact(contactId);
    ret = response.status === 204;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const deleteArticleSupplier = async (articleSupplierId: number) => {
  let ret = false;

  try {
    let response = await BrightMarketApi.deleteArticleSupplier(
      articleSupplierId
    );
    ret = response.status === 204;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const editContact = async (
  contactId: number,
  name: string,
  role: string,
  email: string,
  phoneNumber: string
) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.editContact(
      contactId,
      name,
      role,
      email,
      phoneNumber
    );

    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const editArticleToSupplier = async (
  id: number,
  articleId: number,
  principal: string,
  purchaseUnitId: number,
  commercialTerm?: any
) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.editArticleToSupplier(
      id,
      articleId,
      principal,
      purchaseUnitId,
      commercialTerm
    );

    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const createSkuInArticle = async (
  articleId: string,
  sku: any,
  attribute: any
) => {
  let ret = false;

  try {
    let response = await BrightMarketApi.createSkuInArticle(
      articleId,
      sku,
      attribute ?? []
    );

    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const deleteSkuFromArticle = async (articleSkuId: number) => {
  let ret = false;

  try {
    let response = await BrightMarketApi.deleteSkuFromArticle(articleSkuId);
    ret = response.status === 204;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const uploadSkuPhoto = async (file: File): Promise<string> => {
  const response = await BrightMarketApi.uploadSkuPhoto(file);
  const fileId = response?.data?.data?.fileId;
  if (!fileId) throw new Error("SKU photo upload did not return a file ID");
  return fileId as string;
};

export const getSkuPhotoDownloadUrl = async (fileId: string): Promise<string> => {
  const response = await BrightMarketApi.getSkuPhotoDownloadUrl(fileId);
  return response?.data?.downloadUrl as string;
};

export const editSkuFromArticle = async (
  articleSkuId: number,
  name: string,
  enable: string,
  heightWitPackaging: number,
  lengthWitPackaging: number,
  widthWitPackaging: number,
  weightWitPackaging: number,
  height: number,
  length: number,
  width: number,
  weight: number,
  volumen: number,
  kit: boolean,
  salesUnit: string,
  storageUnit: string,
  size: string,
  colour: number,
  minimumStock: number,
  externalId: string,
  cost: number,
  replenish: boolean,
  currencyId: number,
  photo1: string,
  photo2: string,
  photo3: string
) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.editSkuFromArticle(
      articleSkuId,
      name,
      enable,
      heightWitPackaging,
      lengthWitPackaging,
      widthWitPackaging,
      weightWitPackaging,
      height,
      length,
      width,
      weight,
      volumen,
      kit,
      salesUnit,
      storageUnit,
      size,
      colour,
      minimumStock,
      externalId,
      cost,
      replenish,
      currencyId,
      photo1,
      photo2,
      photo3
    );

    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const editSupplierToArticle = async (
  id: number,
  supplierId: number,
  principal: string,
  purchaseUnitId: number,
  commercialTerm?: any
) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.editSupplierToArticle(
      id,
      supplierId,
      principal,
      purchaseUnitId,
      commercialTerm
    );

    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const getBusinessUnitById = async (businessUnitId: string) => {
  let ret;
  try {
    const response = await BrightMarketApi.getBusinessUnitById(businessUnitId);
    ret = response.data;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const getPricingTypes = async (pagination: number) => {
  const pricingTypes: any[] = [];
  let totalElements = 0;

  try {
    const organizationId = await getOrganizationId();

    const response = await BrightMarketApi.getPricingTypes(
      organizationId,
      pagination
    );

    let items = response.pricingTypes;
    totalElements = response.totalElements;

    items.pricingTypes.forEach(function (item: any) {
      let pricingType = {
        id: item.id,
        code: item.code,
        description: item.description,
      };
      pricingTypes.push(pricingType);
    });
  } catch (e) {
    console.log("We have the error", e);
  }
  return { pricingTypes, totalElements };
};

export const createPricingType = async (code: string, description: string) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.createPricingType(code, description);

    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const deletePricingType = async (pricingTypeId: number) => {
  let ret = false;

  try {
    let response = await BrightMarketApi.deletePricingType(pricingTypeId);
    ret = response.status === 204;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const editPricingType = async (
  pricingTypeId: number,
  code: string,
  description: string
) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.editPricingType(
      pricingTypeId,
      code,
      description
    );

    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const getPromotionTypes = async (pagination: number) => {
  const promotionTypes: any[] = [];
  let totalElements = 0;

  try {
    const organizationId = await getOrganizationId();

    const response = await BrightMarketApi.getPromotionTypes(
      organizationId,
      pagination
    );

    let items = response.promotionTypes;
    totalElements = response.totalElements;

    items.promotionTypes.forEach(function (item: any) {
      let pricingType = {
        id: item.id,
        code: item.code,
        name: item.name,
      };
      promotionTypes.push(pricingType);
    });
  } catch (e) {
    console.log("We have the error", e);
  }
  return { promotionTypes, totalElements };
};

export const createPromotionType = async (code: string, name: string) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.createPromotionType(code, name);

    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const deletePromotionType = async (promotionTypeId: number) => {
  let ret = false;

  try {
    let response = await BrightMarketApi.deletePromotionType(promotionTypeId);
    ret = response.status === 204;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const editPromotionType = async (
  promotionTypeId: number,
  code: string,
  description: string
) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.editPromotionType(
      promotionTypeId,
      code,
      description
    );

    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const deleteBranchOffice = async (branchOfficeId: number) => {
  let ret = false;

  try {
    let response = await BrightMarketApi.deleteBranchOffice(branchOfficeId);
    ret = response.status === 204;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const getBranchOfficeById = async (branchOfficeId: string) => {
  let ret;
  try {
    const response = await BrightMarketApi.getBranchOfficeById(branchOfficeId);
    ret = response.data;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const getBranchOfficeSkusById = async (
  branchOfficeId: string,
  pagination: number,
  skuName: string
) => {
  const organizationId = await getOrganizationId();

  let skus;
  let totalElements = 0;
  try {
    const response = await BrightMarketApi.getBranchOfficeSkusById(
      branchOfficeId,
      pagination,
      organizationId,
      skuName
    );
    skus = response.skus;
    totalElements = response.totalElements;
  } catch (e) {
    console.log("We have the error", e);
  }
  return { skus, totalElements };
};

export const getDepositsSkusById = async (
  depositId: string,
  pagination: number,
  skuName: string
) => {
  const organizationId = await getOrganizationId();

  let skus;
  let totalElements = 0;
  try {
    const response = await BrightMarketApi.getDepositsSkusById(
      depositId,
      pagination,
      organizationId,
      skuName
    );
    skus = response.skus;
    totalElements = response.totalElements;
  } catch (e) {
    console.log("We have the error", e);
  }
  return { skus, totalElements };
};

export const editBranchOffice = async (branchOffice: BranchOffice) => {
  let ret = false;

  try {
    let response = await BrightMarketApi.editBranchOffice(branchOffice);
    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const getAllBranchOfficesTypes = async () => {
  const types: any[] = [];

  try {
    const response = await BrightMarketApi.getAllBranchOfficesTypes();
    const items = response.data;
    items.forEach(function (item: any) {
      let type = {
        value: item.value,
        descriptionEn: item.description_en,
        descriptionEs: item.description_es,
      };
      types.push(type);
    });
  } catch (e) {
    console.log("We have the error", e);
  }
  return types;
};

export const createBranchOffice = async (branchOffice: BranchOffice) => {
  let ret: { success: boolean; branchOffice: any } = {
    success: false,
    branchOffice: null,
  };
  try {
    let response = await BrightMarketApi.createBranchOffice(branchOffice);

    ret.success = response.data.status === StatusResponse.Ok;
    ret.branchOffice = response.data.data;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const getAcceptedPaymentMethods = async (
  pagination: number,
  businessUnitId?: string
) => {
  let acceptedPaymentMethods: any[] = [];
  let totalElements = 0;

  try {
    const organizationId = await getOrganizationId();

    const response = await BrightMarketApi.getAcceptedPaymentMethods(
      organizationId,
      pagination,
      businessUnitId
    );

    let items = response.acceptedPaymentMethods;
    totalElements = response.totalElements;
    acceptedPaymentMethods = items.acceptedPaymentMethod;
  } catch (e) {
    console.log("We have the error", e);
  }
  return { acceptedPaymentMethods, totalElements };
};

export const getAcceptedPaymentMethodById = async (paymentMethodId: string) => {
  let ret;
  try {
    const response = await BrightMarketApi.getAcceptedPaymentMethodById(
      paymentMethodId
    );
    ret = response.data;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const getAllAcceptedPaymentMethods = async () => {
  let clients: any[] = [];

  try {
    const response = await BrightMarketApi.getAllAcceptedPaymentMethods();
    clients = response.data;
  } catch (e) {
    console.log("We have the error", e);
  }
  return clients;
};

export const editAcceptedPaymentMethod = async (
  acceptedPaymentMethodId: number,
  name: string,
  externalId: string,
  typeId: number,
  feeRangeMin: number,
  feeRangeMax: number,
  businessUnitsIds: any
) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.editAcceptedPaymentMethod(
      acceptedPaymentMethodId,
      {
        name,
        externalId,
        typeId,
        feeRangeMin,
        feeRangeMax,
        businessUnitsIds,
      }
    );

    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const getAcceptedPaymentMethodsTypes = async () => {
  const types: any[] = [];

  try {
    const response = await BrightMarketApi.getAcceptedPaymentMethodsTypes();
    let items = response.data;
    items.forEach(function (item: any) {
      let type: RoundingType = {
        id: item.id,
        type: item.type,
        descriptionEs: item.descriptionEs,
        descriptionEn: item.descriptionEn,
      };
      types.push(type);
    });
  } catch (e) {
    console.log("We have the error", e);
  }
  return types;
};

export const getPaymentMethodsByBusinessUnitId = async (
  businessUnitId: number
) => {
  const acceptedPaymentMethods: any[] = [];

  try {
    const response = await BrightMarketApi.getPaymentMethodsByBusinessUnitId(
      businessUnitId
    );
    let items = response.data;
    items.forEach(function (item: any) {
      let pricingType = {
        id: item.id,
        code: item.code,
        name: item.name,
        type: item.type,
        transmitter: item.transmitter,
        feeRange: item.feeRange,
        acceptedGoods: item.acceptedGoods,
      };
      acceptedPaymentMethods.push(pricingType);
    });
  } catch (e) {
    console.log("We have the error", e);
  }
  return acceptedPaymentMethods;
};

export const createAcceptedPaymentMethod = async (paymentMethod: {
  name: string;
  externalId: string;
  typeId: number;
  feeRangeMin: number;
  feeRangeMax: number;
  businessUnitsIds: any;
}) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.createAcceptedPaymentMethod(
      paymentMethod
    );

    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const deleteAcceptedPaymentMethod = async (
  acceptedPaymentMethodId: number
) => {
  let ret = false;

  try {
    let response = await BrightMarketApi.deleteAcceptedPaymentMethod(
      acceptedPaymentMethodId
    );
    ret = response.status === 204;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const deleteAcceptedPaymentMethodFromBusinessUnit = async (
  acceptedPaymentMethodId: number,
  businessUnitId: number
) => {
  let ret = false;

  try {
    let response =
      await BrightMarketApi.deleteAcceptedPaymentMethodFromBusinessUnit(
        acceptedPaymentMethodId,
        businessUnitId
      );
    ret = response.status === 204;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const addAcceptedPaymentMethodToBusinessUnit = async (
  businessUnitId: number,
  acceptedPaymentMethodId: number
) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.addAcceptedPaymentMethodToBusinessUnit(
      businessUnitId,
      acceptedPaymentMethodId
    );

    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const getCompetitors = async (pagination: number) => {
  const competitors: any[] = [];
  let totalElements = 0;

  try {
    const organizationId = await getOrganizationId();

    const response = await BrightMarketApi.getCompetitors(
      organizationId,
      pagination
    );

    let items = response.competitors;
    totalElements = response.totalElements;

    items.competitors.forEach(function (item: any) {
      const competitor = {
        id: item.id,
        name: item.name,
        externalId: item.externalId,
        website: item.website,
        articles: item.articles,
      };
      competitors.push(competitor);
    });
  } catch (e) {
    console.log("We have the error", e);
  }
  return { competitors, totalElements };
};

export const getCompetitorById = async (competitorId: string) => {
  let ret;
  try {
    const response = await BrightMarketApi.getCompetitorById(competitorId);
    ret = response.data;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const deleteCompetitor = async (competitorId: number) => {
  let ret = false;

  try {
    let response = await BrightMarketApi.deleteCompetitor(competitorId);
    ret = response.status === 204;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const createCompetitor = async (
  name: string,
  externalId: string,
  website: string,
  articlesIds: any
) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.createCompetitor(
      name,
      externalId,
      website,
      articlesIds ?? []
    );

    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const editCompetitor = async (
  competitorId: string,
  name: string,
  externalId: string,
  website: string,
  articleIds: any
) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.editCompetitor(
      competitorId,
      name,
      externalId,
      website,
      articleIds
    );

    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const createSalePoint = async (
  branchOfficeId: number,
  name: string,
  acceptedPaymentMethodsIds: any,
  externalId?: string
) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.createSalePoint(
      branchOfficeId,
      name,
      acceptedPaymentMethodsIds,
      externalId
    );

    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const deleteSalePoint = async (salePointId: number) => {
  let ret = false;

  try {
    let response = await BrightMarketApi.deleteSalePoint(salePointId);
    ret = response.status === 204;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const editSalePoint = async (
  salePointId: number,
  name: string,
  acceptedPaymentMethodsIds: any,
  externalId?: string
) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.editSalePoint(
      salePointId,
      name,
      acceptedPaymentMethodsIds ?? [],
      externalId
    );

    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const getDeposits = async (
  pagination: number,
  businessUnitId: string,
  filters: { name: string; externalId: string }
) => {
  const deposits: any[] = [];
  let totalElements = 0;

  try {
    const organizationId = await getOrganizationId();

    const response = await BrightMarketApi.getDeposits(
      organizationId,
      pagination,
      businessUnitId,
      filters
    );

    let items = response.deposits;
    totalElements = response.totalElements;
    items.deposits.forEach(function (item: any) {
      deposits.push(item);
    });
  } catch (e) {
    console.log("We have the error", e);
  }

  return { deposits, totalElements };
};

export const createDeposit = async (deposit: {
  name: string;
  capacity: number;
  externalId: string;
  branchOffices: any;
  address: string;
  businessUnitsIds: any;
  categoriesIds: any;
  latitude: number;
  longitude: number;
  capacityInUnits: number;
  capacityDispatchUnits: number;
  dispatchFrequency: number;
  capacityReception: number;
  storageCostPerUnit: number;
  minimumStock: number;
  maximumStock: number;
}) => {
  let ret: { success: boolean; deposit: any } = {
    success: false,
    deposit: null,
  };
  try {
    let response = await BrightMarketApi.createDeposit(deposit);

    ret.success = response.data.status === StatusResponse.Ok;
    ret.deposit = response.data.data;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const deleteDeposit = async (depositId: number) => {
  let ret = false;

  try {
    let response = await BrightMarketApi.deleteDeposit(depositId);
    ret = response.status === 204;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const editDeposit = async (
  depositId: number,
  deposit: {
    name: string;
    capacity: number;
    externalId: string;
    branchOffices: any;
    address: string;
    businessUnitsIds: any;
    categoriesIds: any;
    capacityInUnits: number;
    capacityDispatchUnits: number;
    dispatchFrequency: number;
    capacityReception: number;
    storageCostPerUnit: number;
    minimumStock: number;
    maximumStock: number;
    latitude: number;
    longitude: number;
  }
) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.editDeposit(depositId, deposit);

    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const getAllBranchOffices = async () => {
  const branchOffices: any[] = [];

  const organziationId = await getOrganizationId();
  try {
    const response = await BrightMarketApi.getAllBranchOffices(organziationId);
    const items = response.branchOffices;
    items?.forEach(function (item: any) {
      let branchOffice = {
        id: item.id,
        name: item.name,
      };
      branchOffices.push(branchOffice);
    });
  } catch (e) {
    console.log("We have the error", e);
  }
  return branchOffices;
};

export const getAllBrands = async () => {
  const brands: any[] = [];

  const organziationId = await getOrganizationId();
  try {
    const response = await BrightMarketApi.getAllBrands(organziationId);
    const items = response.brands;
    items?.forEach(function (item: any) {
      let brand = {
        id: item.id,
        name: item.name,
      };
      brands.push(brand);
    });
  } catch (e) {
    console.log("We have the error", e);
  }
  return brands;
};

export const getAllBranchOfficesByBu = async (businessUnitIds: any) => {
  const branchOffices: any[] = [];

  try {
    const response = await BrightMarketApi.getAllBranchOfficesByBu(
      businessUnitIds
    );
    return response;
  } catch (e) {
    console.log("We have the error", e);
  }
  return branchOffices;
};

export const getBranchOffices = async (pagination: number) => {
  let branchOffices: any[] = [];
  let totalElements = 0;

  try {
    const organizationId = await getOrganizationId();

    const response = await BrightMarketApi.getBranchOffices(
      organizationId,
      pagination
    );

    let items = response.branchOffices;
    totalElements = response.totalElements;
    branchOffices = items.branchOffices;
  } catch (e) {
    console.log("We have the error", e);
  }
  return { branchOffices, totalElements };
};

export const getDepositById = async (depositId: string) => {
  let ret;
  try {
    const response = await BrightMarketApi.getDepositById(depositId);
    ret = response.data;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const getDepositValuedInventory = async (depositId: string) => {
  let value = 0;
  try {
    const response = await BrightMarketApi.getDepositValuedInventory(depositId);
    value = response ?? 0;
  } catch (e) {
    console.log("We have the error", e);
  }
  return value;
};

export const getDepositSchedulesById = async (
  depositId: string,
  pagination: number
) => {
  let schedules: any[] = [];
  let totalElements = 0;

  try {
    const response = await BrightMarketApi.getDepositSchedules(
      depositId,
      pagination
    );
    schedules = response.data;
    totalElements = response.totalElements;
  } catch (e) {
    console.log("We have the error", e);
  }

  return { schedules, totalElements };
};

export const deleteArticleStock = async (
  articleStockId: number,
  type: string
) => {
  let ret = false;

  try {
    let response = await BrightMarketApi.deleteArticleStock(
      articleStockId,
      type
    );
    ret = response.status === 204;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const addArticleStock = async (
  stock: number,
  skuId: number,
  type: string,
  placeId: number,
  minimumStock?: number,
  leadTime?: number
) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.addArticleStock(
      stock,
      skuId,
      type,
      placeId,
      minimumStock,
      leadTime
    );

    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const getPurchaseOrdersByOrganization = async (
  pagination: number,
  supplierId?: number | null,
  status?: string | null,
  articlesId?: string | null,
  startDate?: string | null,
  endDate?: string | null
) => {
  const purchaseOrders: any[] = [];
  let totalElements = 0;

  try {
    const response = await BrightMarketApi.getPurchaseOrdersByOrganization(
      pagination,
      supplierId,
      status,
      articlesId,
      startDate,
      endDate
    );

    const items = response;
    totalElements = response.totalElements;

    items.purchaseOrders.forEach(function (item: any) {
      purchaseOrders.push(item);
    });
  } catch (e) {
    console.log("We have the error", e);
  }
  return { purchaseOrders, totalElements };
};

export const createPurchaseOrder = async (
  body: any,
  preview: boolean,
  businessUnitId?: string
) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.createPurchaseOrder(
      body,
      preview,
      businessUnitId
    );

    if (response.data.status === StatusResponse.Ok) return response.data;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const getPurchaseOrderStatics = async () => {
  let ret;
  try {
    const response = await BrightMarketApi.getPurchaseOrderStatics();

    ret = response.data;
  } catch (e) {
    console.log("We have the error", e);
  }

  return ret;
};

export const getAllDeposits = async () => {
  const deposits: any[] = [];

  try {
    const response = await BrightMarketApi.getAllDeposits();
    const items = response.data.data;
    items.forEach(function (item: any) {
      let deposit = {
        id: item.id,
        name: item.name,
      };
      deposits.push(deposit);
    });
  } catch (e) {
    console.log("We have the error", e);
  }
  return deposits;
};

export const deletePurchaseOrder = async (purchaseOrderId: number) => {
  let ret = false;

  try {
    let response = await BrightMarketApi.deletePurchaseOrder(purchaseOrderId);
    ret = response.status === 204;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const getPurchaseOrderById = async (purchaseOrderId: string) => {
  let ret;
  try {
    const response = await BrightMarketApi.getPurchaseOrderById(
      purchaseOrderId
    );
    ret = response.data;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const markReceivedOrder = async (receivedOrder: any) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.markReceivedOrder(receivedOrder);

    if (response.data.status === StatusResponse.Ok) return response.data;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const markReceivedRepositionOrder = async (receivedOrder: any) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.markReceivedRepositionOrder(
      receivedOrder
    );

    if (response.data.status === StatusResponse.Ok) return response.data;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const markSentArticlesRepositionOrder = async (
  repositionOrderSkuIds: any,
  arriveDate: string
) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.markSentArticlesRepositionOrder(
      repositionOrderSkuIds,
      arriveDate
    );

    if (response.data.status === StatusResponse.Ok) return response.data;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const editPurchaseOrderStatus = async (
  purchaseOrderId: number,
  status: string,
  notCompletedReason?: string
) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.editPurchaseOrderStatus(
      purchaseOrderId,
      status,
      notCompletedReason
    );

    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const editArticleStock = async (
  articleStockId: number,
  stock: string,
  type: string
) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.editArticleStock(
      articleStockId,
      stock,
      type
    );

    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const getRepositionOrdersByOrganization = async (query: {
  pagination: number;
  status?: string | null;
  startCreatedDate?: string | null;
  endCreatedDate?: string | null;
  depositTo?: number | null;
  branchOfficeTo?: number | null;
  depositFrom?: number | null;
  branchOfficeFrom?: number | null;
  orderBy?: string;
  businessUnitId?: string;
  articlesIds?: number[];
  skuId?: string | null;
}) => {
  const repositionOrders: any[] = [];
  let totalElements = 0;

  try {
    const response = await BrightMarketApi.getRepositionOrdersByOrganization(
      query
    );

    const items = response;
    totalElements = response.totalElements;

    items.repositionOrders.forEach(function (item: any) {
      repositionOrders.push(item);
    });
  } catch (e) {
    console.log("We have the error", e);
  }
  return { repositionOrders, totalElements };
};

export const deleteRepositionOrder = async (repositionOrderId: number) => {
  let ret = false;

  try {
    let response = await BrightMarketApi.deleteRepositionOrder(
      repositionOrderId
    );
    ret = response.status === 204;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const editRepositionOrderStatus = async (
  repositionOrderId: number,
  status: string
) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.editRepositionOrderStatus(
      repositionOrderId,
      status
    );

    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const getRepositionOrderStatics = async () => {
  let ret;
  try {
    const response = await BrightMarketApi.getRepositionOrderStatics();

    ret = response.data;
  } catch (e) {
    console.log("We have the error", e);
  }

  return ret;
};

export const getRepositionOrderById = async (repositionOrderId: string) => {
  let ret;
  try {
    const response = await BrightMarketApi.getRepositionOrderById(
      repositionOrderId
    );
    ret = response.data;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const createRepositionOrder = async (
  body: any,
  preview: boolean,
  businessUnitId?: string
) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.createRepositionOrder(
      body,
      preview,
      businessUnitId
    );

    if (response.data.status === StatusResponse.Ok) return response.data;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const createRepositionOrderDraft = async (
  orders: {
    skuId: number;
    branchOfficeOrDepositFrom: string;
    branchOfficeOrDepositFromId: number;
    branchOfficeOrDepositTo: string;
    branchOfficeOrDepositToId: number;
    quantity: number;
  }[],
  businessUnitId: string
) => {
  let ret = { success: false };
  try {
    let response = await BrightMarketApi.createRepositionOrderDraft(
      orders,
      businessUnitId
    );

    if (response.data.status === StatusResponse.Ok)
      return { data: response.data, success: true };
    else {
      return { success: false };
    }
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const createPurchaseOrderDraft = async (
  orders: {
    skuId: number;
    branchOfficeOrDeposit: string;
    branchOfficeOrDepositId: number;
    supplierId: string;
    quantity: number;
  }[],
  businessUnitId: string
) => {
  let ret = { success: false };
  try {
    let response = await BrightMarketApi.createPurchaseOrderDraft(
      orders,
      businessUnitId
    );

    if (response.data.status === StatusResponse.Ok)
      return { data: response.data, success: true };
    else {
      return { success: false };
    }
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const getRepositionOrderDraft = async (businessUnitId: string) => {
  let ret;
  try {
    const response = await BrightMarketApi.getRepositionOrderDraft(
      businessUnitId
    );
    ret = response.data;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const getPurchaseOrderDraft = async (businessUnitId: string) => {
  let ret;
  try {
    const response = await BrightMarketApi.getPurchaseOrderDraft(
      businessUnitId
    );
    ret = response.data;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const getClients = async (
  pagination: number,
  filters: {
    clientId?: string;
    idNumber?: string;
    name?: string;
    email?: string;
    mobile?: string;
    segmentId?: string;
    levelId?: string;
    birthStartDate?: string;
    birthEndDate?: string;
    registerStartDate?: string;
    registerEndDate?: string;
  },
  size: number = 10
) => {
  let clients: any[] = [];
  let totalElements = 0;

  try {
    const organizationId = await getOrganizationId();

    const response = await BrightMarketApi.getClients(
      organizationId,
      pagination,
      filters,
      size
    );

    let items = response._embedded?.clients || [];
    totalElements = response.totalElements;
    clients = items;
  } catch (e) {
    console.log("We have the error", e);
  }
  return { clients, totalElements };
};

/**
 * Search clients by LIKE filters (ID, name, email, idNumber, mobile).
 * Uses dedicated backend endpoint so e.g. filtering by only ID returns correct results.
 */
export const getClientsByLikeFilters = async (
  page: number,
  size: number,
  filters: {
    clientId?: string;
    name?: string;
    email?: string;
    idNumber?: string;
    mobile?: string;
  }
) => {
  let clients: any[] = [];
  let totalElements = 0;
  try {
    const result = await BrightMarketApi.getClientsByLikeFilters(page, size, filters);
    clients = result.clients || [];
    totalElements = result.totalElements ?? 0;
  } catch (e) {
    console.log("We have the error", e);
  }
  return { clients, totalElements };
};

export const getClientsList = async (
  pagination: number,
  filters: {
    idNumber?: string;
    externalId?: string;
    segmentId?: string;
    levelId?: string;
    clusterId?: string;
    branchOfficeId?: string;
    birthStartDate?: string;
    birthEndDate?: string;
    registerStartDate?: string;
    registerEndDate?: string;
  },
  size: number = 10
) => {
  try {
    return await BrightMarketApi.getClientsList(pagination, filters, size);
  } catch (e) {
    console.log("We have the error", e);
    return { clients: [] };
  }
};

export const getClientsListCount = async (filters: {
  idNumber?: string;
  externalId?: string;
  segmentId?: string;
  levelId?: string;
  clusterId?: string;
  branchOfficeId?: string;
  birthStartDate?: string;
  birthEndDate?: string;
  registerStartDate?: string;
  registerEndDate?: string;
}) => {
  try {
    return await BrightMarketApi.getClientsListCount(filters);
  } catch (e) {
    console.log("We have the error", e);
    return { totalElements: 0 };
  }
};

export const getClientsForList = async (
  pagination: number,
  filters: {
    idNumber?: string;
    externalId?: string;
    segmentId?: string;
    levelId?: string;
    clusterId?: string;
    branchOfficeId?: string;
    birthStartDate?: string;
    birthEndDate?: string;
    registerStartDate?: string;
    registerEndDate?: string;
  }
) => {
  let clients: any[] = [];
  let totalElements = 0;

  try {
    const organizationId = await getOrganizationId();

    const response = await BrightMarketApi.getClientsForList(
      organizationId,
      pagination,
      filters
    );

    let items = response._embedded?.clients || [];
    totalElements = response.totalElements;
    clients = items;
  } catch (e) {
    console.log("We have the error", e);
  }
  return { clients, totalElements };
};

export const exportClientsToCsv = async (filters: {
  idNumber?: string;
  segmentId?: string;
  levelId?: string;
  birthStartDate?: string;
  birthEndDate?: string;
  registerStartDate?: string;
  registerEndDate?: string;
}) => {
  try {
    const organizationId = await getOrganizationId();
    const response = await BrightMarketApi.exportClientsToCsv(
      organizationId,
      filters
    );
    return response;
  } catch (e) {
    console.log("We have the error", e);
    throw e;
  }
};

export const deleteClient = async (clientId: number) => {
  let ret = false;

  try {
    let response = await BrightMarketApi.deleteClient(clientId);
    ret = response.status === 204;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const createClient = async (clientInfo: {
  idNumber: string;
  birthdate: string;
  documentType: string;
  country: string;
  city: string;
  sex: string;
  active: boolean;
  levelId?: string;
  segments?: string[];
  name?: string;
  lastName?: string;
  phone?: string;
  mobile?: string;
  address?: string;
  email?: string;
  termsConditions?: boolean;
  storeOnlyContactValidation?: boolean;
}) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.createClient(clientInfo);
    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const editClient = async (clientInfo: {
  id: number;
  externalId?: string;
  birthdate: string;
  documentType: string;
  documentNumber: string;
  country: string;
  city: string;
  sex: string;
  active: boolean;
  name?: string;
  lastName?: string;
  phone?: string;
  mobile?: string;
  address?: string;
  email?: string;
  termsConditions?: boolean;
  storeOnlyContactValidation?: boolean;
}) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.editClient(clientInfo);

    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const getClientById = async (clientId: string) => {
  let ret;
  try {
    const response = await BrightMarketApi.getClientById(clientId);
    ret = response.data;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const getProductsRecommendationsByClientId = async (
  pagination: number,
  id: string
) => {
  let recommendations: any[] = [];
  let totalElements = 0;

  try {
    const organizationId = await getOrganizationId();

    const response = await BrightMarketApi.getProductsRecommendationsByClientId(
      organizationId,
      pagination,
      id
    );

    recommendations = response.recommendations;
    totalElements = response.totalElements;
  } catch (e) {
    console.log("We have the error", e);
  }
  return { recommendations, totalElements };
};

export const deleteProductRecommendation = async (
  clientId: string,
  productRecommendationId: number
) => {
  let ret = false;

  try {
    let response = await BrightMarketApi.deleteProductRecommendation(
      clientId,
      productRecommendationId
    );
    ret = response.status === 204;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const getClientMovementsById = async (
  clientId: string,
  page: number
) => {
  let points: any[] = [];
  let totalElements = 0;

  try {
    const response = await BrightMarketApi.getClientMovementsById(
      clientId,
      page
    );
    points = response.points;
    totalElements = response.totalElements;
  } catch (e) {
    console.log("We have the error", e);
  }
  return { points, totalElements };
};

export const addMovementToClient = async (
  clientId: number,
  movement: {
    type: string;
    pointsMoved: number;
    comments: string;
    expirationDate?: string;
  }
) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.addMovementToClient(
      clientId,
      movement
    );

    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const getPointsSummaryByClientId = async (clientId: number) => {
  try {
    return await BrightMarketApi.getPointsSummaryByClientId(clientId);
  } catch (e) {
    console.log("We have the error", e);
    return null;
  }
};

export const setPointsToClient = async (
  clientId: number,
  units: { activePoints: number; pendingPoints: number }
) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.setPointsToClient(clientId, units);

    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const getClientStatsByClientId = async (clientId: string) => {
  let ret;
  const organizationId = await getOrganizationId();

  try {
    const response = await BrightMarketApi.getClientStatsByClientId(
      organizationId,
      clientId
    );
    ret = response.data;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const getPurchasedItemsByClientId = async (
  pagination: number,
  clientId: string
) => {
  let purchasedSkus: any[] = [];
  let purchasedBrands: any[] = [];
  let purchasedCategories: any[] = [];
  let totalSkus = 0;
  let totalBrands = 0;
  let totalCategories = 0;

  try {
    const organizationId = await getOrganizationId();

    const response = await BrightMarketApi.getPurchasedItemsByClientId(
      organizationId,
      pagination,
      clientId
    );

    purchasedSkus = response.purchasedSkus || [];
    purchasedBrands = response.purchasedBrands || [];
    purchasedCategories = response.purchasedCategories || [];
    totalSkus = response.totalSkus || 0;
    totalBrands = response.totalBrands || 0;
    totalCategories = response.totalCategories || 0;
  } catch (e) {
    console.log("We have the error", e);
  }
  return {
    purchasedSkus,
    purchasedBrands,
    purchasedCategories,
    totalSkus,
    totalBrands,
    totalCategories,
  };
};

export const getAllClients = async () => {
  let clients: any[] = [];

  try {
    const response = await BrightMarketApi.getAllClients();
    clients = response.data;
  } catch (e) {
    console.log("We have the error", e);
  }
  return clients;
};

export const getValidationStatistics = async () => {
  let statistics: any = null;

  try {
    const response = await BrightMarketApi.getValidationStatistics();
    if (response?.data) {
      statistics = response.data;
    }
  } catch (e) {
    console.log("We have the error", e);
  }
  return statistics;
};

export const getClientSegments = async (pagination: number) => {
  let clientSegments: any[] = [];
  let totalElements = 0;

  try {
    const organizationId = await getOrganizationId();

    const response = await BrightMarketApi.getClientSegments(
      organizationId,
      pagination
    );

    let items = response.clientSegments;
    totalElements = response.totalElements;

    clientSegments = items;
  } catch (e) {
    console.log("We have the error", e);
  }
  return { clientSegments, totalElements };
};

export const deleteClientSegment = async (clientSegmentId: number) => {
  let ret = false;

  try {
    let response = await BrightMarketApi.deleteClientSegment(clientSegmentId);
    ret = response.status === 204;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const createClientSegment = async (
  segment:
    | {
        name: string;
        description: string;
        typeInt?: number;
        type: "DYNAMIC";
        sex: string;
        minAge: number;
        maxAge: number;
        averageTicketMin: number;
        averageTicketMax: number;
        purchaseChannelId: number;
        purchaseCountPeriod: number;
        purchaseCountLastDays: number;
        purchaseCountRange: number;
        purchaseCountStartDate: string;
        purchaseCountEndDate: string;
        totalSpent: number;
        birthdayStartDate: string;
        birthdayEndDate: string;
        programRegistrationStartDate: string;
        programRegistrationEndDate: string;
        active: boolean;
        articleIds: string[];
        skuId: string; //[]
        brandIds: string[];
        clientLevelId: string;
      }
    | {
        name: string;
        description: string;
        typeInt?: number;
        type: "STATIC";
        active: boolean;
        clientIds: string[];
      }
) => {
  let ret: any = false;
  try {
    let response = await BrightMarketApi.createClientSegment(segment);

    if (response.data.status === StatusResponse.Ok) {
      ret = response.data.data; // Retornar el segmento creado
    }
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const previewClientSegment = async (
  segment: {
    name: string;
    description: string;
    typeInt?: number;
    type: "DYNAMIC";
    sex: string;
    minAge: number;
    maxAge: number;
    averageTicket: number;
    purchaseChannelId: number;
    purchaseCountPeriod: number;
    purchaseCountLastDays: number;
    purchaseCountRange: number;
    purchaseCountStartDate: string;
    purchaseCountEndDate: string;
    totalSpent: number;
    birthdayStartDate: string;
    birthdayEndDate: string;
    programRegistrationStartDate: string;
    programRegistrationEndDate: string;
    active: boolean;
    articleIds: string[];
    skuId: string; //[]
    brandIds: string[];
    clientLevelId: string;
  },
  page: number,
  size: number
) => {
  try {
    let response = await BrightMarketApi.previewClientSegment(segment, page, size);
    return {
      clients: response.data.data.content,
      success: response.data.status === StatusResponse.Ok,
      totalElements: response.data.data.totalElements,
    };
  } catch (e: any) {
    console.log("We have the error", e);
    const apiError = e?.response?.data?.apierror;
    const isTimeout =
      e?.code === "ECONNABORTED" || String(e?.message || "").toLowerCase().includes("timeout");
    return {
      clients: [],
      success: false,
      totalElements: 0,
      message:
        apiError?.message_ES ??
        apiError?.message ??
        e?.response?.data?.message_ES ??
        e?.response?.data?.message ??
        (isTimeout ? "ClientSegment-PreviewTimeout" : undefined),
      messageKey: isTimeout ? "ClientSegment-PreviewTimeout" : undefined,
    };
  }
};

export const editClientSegment = async (
  id: number,
  segment:
    | {
        name: string;
        description: string;
        type: "DYNAMIC";
        sex: string;
        minAge: number;
        maxAge: number;
        averageTicketMin: number;
        averageTicketMax: number;
        purchaseChannelId: number;
        purchaseCountPeriod: number;
        purchaseCountLastDays: number;
        purchaseCountRange: number;
        purchaseCountStartDate: string;
        purchaseCountEndDate: string;
        totalSpent: number;
        birthdayStartDate: string;
        birthdayEndDate: string;
        programRegistrationStartDate: string;
        programRegistrationEndDate: string;
        active: boolean;
        articleIds: string[];
        skuId: string; //[]
        brandIds: string[];
        clientLevelId: string;
      }
    | {
        name: string;
        description: string;
        type: "STATIC";
        active: boolean;
        clientIds: string[];
      }
) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.editClientSegment(id, segment);

    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const cloneClientSegment = async (clientSegmentId: number) => {
  let ret = false;
  let segment;
  try {
    let response = await BrightMarketApi.cloneClientSegment(clientSegmentId);
    ret = response.data.status === StatusResponse.Ok;
    segment = response.data.data;
  } catch (e) {
    console.log("We have the error", e);
  }
  return { success: ret, segment };
};

export const getClientSegmentById = async (
  clientSegmentId: string,
  clientPage: number
) => {
  let ret;
  try {
    const response = await BrightMarketApi.getClientSegmentById(
      clientSegmentId,
      clientPage
    );

    ret = {
      ...response.data.data,
      clientsPagination: response.data.clientsPagination,
    };
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const getClientsBySegmentId = async (
  clientSegmentId: string,
  pagination: number,
  size: number = 10
) => {
  let ret;
  try {
    const response = await BrightMarketApi.getClientsBySegmentId(
      clientSegmentId,
      pagination,
      size
    );
    ret = response;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const addClientsToClientSegment = async (
  clientSegmentId: number,
  clientId: number
) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.addClientsToClientSegment(
      clientSegmentId,
      clientId
    );
    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const deleteClientFromClientSegment = async (
  clientSegmentId: number,
  clientId: number
) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.deleteClientFromClientSegment(
      clientSegmentId,
      clientId
    );
    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const getPromotions = async (
  pagination: number,
  filters?: { name?: string; externalId?: string; status?: string; type?: string; origin?: string; brandId?: number }
) => {
  let promotions: any[] = [];
  let totalElements = 0;

  try {
    const organizationId = await getOrganizationId();

    const response = await BrightMarketApi.getPromotions(
      organizationId,
      pagination,
      filters
    );

    promotions = response.promotions.promotions;
    totalElements = response.totalElements;
  } catch (e) {
    console.log("We have the error", e);
  }
  return { promotions, totalElements };
};

export const getPromotionsUsedByClientId = async (
  pagination: number,
  clientId: string
) => {
  let promotions: any[] = [];
  let totalElements = 0;

  try {
    const organizationId = await getOrganizationId();

    const response = await BrightMarketApi.getPromotionsUsedByClientId(
      organizationId,
      pagination,
      clientId
    );

    promotions = response.promotions;
    totalElements = response.totalElements;
  } catch (e) {
    console.log("We have the error", e);
  }
  return { promotions, totalElements };
};

export const getPromotionsNoDate = async (pagination: number) => {
  let promotions: any[] = [];
  let totalElements = 0;

  try {
    const organizationId = await getOrganizationId();

    const response = await BrightMarketApi.getPromotionsNoDate(
      organizationId,
      pagination
    );

    promotions = response.promotions.promotions;
    totalElements = response.totalElements;
  } catch (e) {
    console.log("We have the error", e);
  }
  return { promotions, totalElements };
};

export const getPromotionsByMonth = async (month: string) => {
  let promotions: any[] = [];

  try {
    const response = await BrightMarketApi.getPromotionsByMonth(month);

    promotions = response.data;
  } catch (e) {
    console.log("We have the error", e);
  }
  return promotions;
};

export const deletePromotion = async (promotionId: number) => {
  let ret = false;

  try {
    let response = await BrightMarketApi.deletePromotion(promotionId);
    ret = response.status === 204;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const cancelPromotion = async (promotionId: number) => {
  let ret = false;

  try {
    let response = await BrightMarketApi.cancelPromotion(promotionId);
    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const getPromotionById = async (promotionId: string) => {
  let ret;
  try {
    const response = await BrightMarketApi.getPromotionById(promotionId);
    ret = response.data;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const getPromotionRecommendationContext = async (promotionId: string) => {
  try {
    return await BrightMarketApi.getPromotionRecommendationContext(promotionId);
  } catch (e) {
    console.log("We have the error", e);
    return null;
  }
};

export const completePromotionFromRecommendation = async (
  promotionId: string,
  body: {
    name?: string;
    description?: string;
    startDate?: string;
    endDate?: string;
    skuIds?: number[];
    branchOfficeIds?: number[];
    clientSegmentIds?: number[];
  }
) => {
  try {
    const response = await BrightMarketApi.completePromotionFromRecommendation(
      promotionId,
      body
    );
    return response.data;
  } catch (e: any) {
    const apiError = e?.response?.data?.apierror;
    const msg =
      apiError?.message_ES ??
      apiError?.message ??
      e?.response?.data?.message ??
      "Error";
    throw new Error(msg);
  }
};

export const getRegularPromotionSkus = async (
  promotionId: string,
  pagination: number,
  name?: string
) => {
  let skus: any[] = [];
  let totalElements = 0;
  try {
    const response = await BrightMarketApi.getRegularPromotionSkus(
      promotionId,
      pagination,
      name
    );
    skus = response.skus;
    totalElements = response.totalElements;
  } catch (e) {
    console.log("We have the error", e);
  }
  return { skus, totalElements };
};

export const createPromotionBuyTogether = async (
  name: string,
  externalId: string,
  description: string,
  recurrence: any,
  startDate: any,
  endDate: any,
  discountFirstList: string,
  discountSecondList: string,
  skuIdFirstList: any,
  skuIdSecondList: any,
  branchOfficeIds: any,
  clientSegmentIds: any,
  categoryIds: any,
  brandIds: any,
  acceptedPaymentMethodIds: any,
  bines: any,
  binMin: number,
  binMax: number,
  minimumTicket: number,
  maximumTicket: number,
  quantityApplications: number,
  quantityPerArticle: number,
  quantityPerBranchOffice: number,
  quantityPerClient: number,
  accumulateOtherPromotions: boolean,
  distributeLines: boolean
) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.createPromotionBuyTogether(
      name,
      externalId,
      description,
      recurrence,
      startDate,
      endDate,
      discountFirstList,
      discountSecondList,
      skuIdFirstList,
      skuIdSecondList,
      branchOfficeIds,
      clientSegmentIds,
      categoryIds,
      brandIds,
      acceptedPaymentMethodIds,
      bines,
      binMin,
      binMax,
      minimumTicket,
      maximumTicket,
      quantityApplications,
      quantityPerArticle,
      quantityPerBranchOffice,
      quantityPerClient,
      accumulateOtherPromotions,
      distributeLines
    );
    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const createPromotionMxN = async (
  name: string,
  externalId: string,
  description: string,
  recurrence: any,
  startDate: any,
  endDate: any,
  skuIds: any,
  quantityForPromotion: string,
  quantityWithDiscount: string,
  discountPercent: string,
  allProducts: boolean,
  highPrice: boolean,
  forTotalArticle: boolean,
  branchOfficeIds: any,
  clientSegmentIds: any,
  categoryIds: any,
  brandIds: any,
  acceptedPaymentMethodIds: any,
  bines: any,
  binMin: number,
  binMax: number,
  minimumTicket: number,
  maximumTicket: number,
  quantityApplications: number,
  quantityPerArticle: number,
  quantityPerBranchOffice: number,
  quantityPerClient: number,
  accumulateOtherPromotions: boolean,
  distributeLines: boolean
) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.createPromotionMxN(
      name,
      externalId,
      description,
      recurrence,
      startDate,
      endDate,
      skuIds,
      quantityForPromotion,
      quantityWithDiscount,
      discountPercent,
      allProducts,
      highPrice,
      forTotalArticle,
      branchOfficeIds,
      clientSegmentIds,
      categoryIds,
      brandIds,
      acceptedPaymentMethodIds,
      bines,
      binMin,
      binMax,
      minimumTicket,
      maximumTicket,
      quantityApplications,
      quantityPerArticle,
      quantityPerBranchOffice,
      quantityPerClient,
      accumulateOtherPromotions,
      distributeLines
    );
    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const createPromotionRegular = async (promoInfo: {
  name: string;
  externalId: string;
  description: string;
  recurrence: any;
  startDate: any;
  endDate: any;
  regularPromotionType: string;
  branchOfficeIds: any;
  clientSegmentIds: any;
  categoryIds: any;
  brandIds: any;
  acceptedPaymentMethodIds: any;
  bines: any;
  binMin: number;
  binMax: number;
  skuIds: any;
  minimumTicket: number;
  maximumTicket: number;
  quantityApplications: number;
  quantityPerArticle: number;
  quantityPerBranchOffice: number;
  quantityPerClient: number;
  accumulateOtherPromotions: boolean;
  distributeLines: boolean;
  discountValue?: string;
  discountPercentage?: any;
  giftId?: string;
}) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.createPromotionRegular(promoInfo);
    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const createPromotionBogo = async (
  name: string,
  externalId: string,
  description: string,
  recurrence: any,
  startDate: any,
  endDate: any,
  skuIds: any,
  discountType: string,
  giftId: string,
  branchOfficeIds: any,
  clientSegmentIds: any,
  categoryIds: any,
  brandIds: any,
  acceptedPaymentMethodIds: any,
  bines: any,
  binMin: number,
  binMax: number,
  minimumTicket: number,
  maximumTicket: number,
  quantityApplications: number,
  quantityPerArticle: number,
  quantityPerBranchOffice: number,
  quantityPerClient: number,
  accumulateOtherPromotions: boolean,
  distributeLines: boolean
) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.createPromotionBogo(
      name,
      externalId,
      description,
      recurrence,
      startDate,
      endDate,
      skuIds,
      discountType,
      giftId,
      branchOfficeIds,
      clientSegmentIds,
      categoryIds,
      brandIds,
      acceptedPaymentMethodIds,
      bines,
      binMin,
      binMax,
      minimumTicket,
      maximumTicket,
      quantityApplications,
      quantityPerArticle,
      quantityPerBranchOffice,
      quantityPerClient,
      accumulateOtherPromotions,
      distributeLines
    );
    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const createPromotionProgressive = async (
  name: string,
  externalId: string,
  description: string,
  recurrence: any,
  startDate: any,
  endDate: any,
  skuIds: any,
  lines: any,
  branchOfficeIds: any,
  clientSegmentIds: any,
  categoryIds: any,
  brandIds: any,
  acceptedPaymentMethodIds: any,
  bines: any,
  binMin: number,
  binMax: number,
  minimumTicket: number,
  maximumTicket: number,
  quantityApplications: number,
  quantityPerArticle: number,
  quantityPerBranchOffice: number,
  quantityPerClient: number,
  accumulateOtherPromotions: boolean,
  distributeLines: boolean
) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.createPromotionProgressive(
      name,
      externalId,
      description,
      recurrence,
      startDate,
      endDate,
      skuIds,
      lines,
      branchOfficeIds,
      clientSegmentIds,
      categoryIds,
      brandIds,
      acceptedPaymentMethodIds,
      bines,
      binMin,
      binMax,
      minimumTicket,
      maximumTicket,
      quantityApplications,
      quantityPerArticle,
      quantityPerBranchOffice,
      quantityPerClient,
      accumulateOtherPromotions,
      distributeLines
    );
    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const editPromotionRegular = async (
  id: number,
  promo: {
    promotion: {
      name: string;
      externalId: string;
      description: string;
      recurrence: any;
      startDate: any;
      endDate: any;
      branchOfficeIds: any;
      clientSegmentIds: any;
      categoryIds: any;
      brandIds: any;
      acceptedPaymentMethodIds: any;
      bines: any;
      binMin: number;
      binMax: number;
      minimumTicket: number;
      maximumTicket: number;
      quantityApplications: number;
      quantityPerArticle: number;
      quantityPerBranchOffice: number;
      quantityPerClient: number;
      accumulateOtherPromotions: boolean;
      distributeLines: boolean;
    };
    discountValue?: string;
    discountPercent?: any;
    giftId?: string;
    regularPromotionType: string;
    skuIds: any;
  }
) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.editPromotionRegular(id, promo);
    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const editPromotionBuyTogether = async (
  id: number,
  promo: {
    promotion: {
      name: string;
      externalId: string;
      description: string;
      recurrence: any;
      startDate: any;
      endDate: any;
      branchOfficeIds: any;
      clientSegmentIds: any;
      categoryIds: any;
      brandIds: any;
      acceptedPaymentMethodIds: any;
      bines: any;
      binMin: number;
      binMax: number;
      minimumTicket: number;
      maximumTicket: number;
      quantityApplications: number;
      quantityPerArticle: number;
      quantityPerBranchOffice: number;
      quantityPerClient: number;
      accumulateOtherPromotions: boolean;
      distributeLines: boolean;
    };
    discountFirstList: number;
    discountSecondList: number;
    skuIdFirstList: any;
    skuIdSecondList: any;
  }
) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.editPromotionBuyTogether(id, promo);
    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const editPromotionMxN = async (
  id: number,
  promo: {
    promotion: {
      name: string;
      description: string;
      externalId: string;
      recurrence: any;
      startDate: any;
      endDate: any;
      branchOfficeIds: any;
      clientSegmentIds: any;
      categoryIds: any;
      brandIds: any;
      acceptedPaymentMethodIds: any;
      bines: any;
      binMin: number;
      binMax: number;
      minimumTicket: number;
      maximumTicket: number;
      quantityApplications: number;
      quantityPerArticle: number;
      quantityPerBranchOffice: number;
      quantityPerClient: number;
      accumulateOtherPromotions: boolean;
      distributeLines: boolean;
    };
    discountPercent: number;
    quantityWithDiscount: number;
    quantityForPromotion: number;
    allProducts: boolean;
    forTotalArticles: boolean;
    highPrice: boolean;
    skuIds: any;
  }
) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.editPromotionMxN(id, promo);
    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const editPromotionProgressive = async (
  id: number,
  promo: {
    promotion: {
      name: string;
      externalId: string;
      description: string;
      recurrence: any;
      startDate: any;
      endDate: any;
      branchOfficeIds: any;
      clientSegmentIds: any;
      categoryIds: any;
      brandIds: any;
      acceptedPaymentMethodIds: any;
      bines: any;
      binMin: number;
      binMax: number;
      minimumTicket: number;
      maximumTicket: number;
      quantityApplications: number;
      quantityPerArticle: number;
      quantityPerBranchOffice: number;
      quantityPerClient: number;
      accumulateOtherPromotions: boolean;
      distributeLines: boolean;
    };
    lines: any;
    skuIds: any;
  }
) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.editPromotionProgressive(id, promo);
    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const editPromotionBogo = async (
  id: number,
  promo: {
    promotion: {
      name: string;
      externalId: string;
      description: string;
      recurrence: any;
      startDate: any;
      endDate: any;
      branchOfficeIds: any;
      clientSegmentIds: any;
      categoryIds: any;
      brandIds: any;
      acceptedPaymentMethodIds: any;
      bines: any;
      binMin: number;
      binMax: number;
      minimumTicket: number;
      maximumTicket: number;
      quantityApplications: number;
      quantityPerArticle: number;
      quantityPerBranchOffice: number;
      quantityPerClient: number;
      accumulateOtherPromotions: boolean;
      distributeLines: boolean;
    };
    skuIds: any;
    type: string;
    giftId: string;
  }
) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.editPromotionBogo(id, promo);
    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const getBrands = async (
  pagination: number,
  filters: { name: string; externalId: string }
) => {
  let brands: any[] = [];
  let totalElements = 0;

  try {
    const organizationId = await getOrganizationId();

    const response = await BrightMarketApi.getBrands(
      organizationId,
      pagination,
      filters
    );

    let items = response.brands;
    totalElements = response.totalElements;
    brands = items.brands;
  } catch (e) {
    console.log("We have the error", e);
  }
  return { brands, totalElements };
};

export const deleteBrand = async (brandId: number) => {
  let ret = false;

  try {
    let response = await BrightMarketApi.deleteBrand(brandId);
    ret = response.status === 204;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const createBrand = async (
  name: string,
  externalId: string,
  description: string
) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.createBrand(
      name,
      externalId,
      description
    );

    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const editBrand = async (
  brandId: number,
  name: string,
  externalId: string,
  description: string
) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.editBrand(
      brandId,
      name,
      externalId,
      description
    );

    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const getAllIdTypes = async () => {
  let idTypes: any[] = [];

  try {
    const response = await BrightMarketApi.getAllIdTypes();
    idTypes = response.idTypes;
  } catch (e) {
    console.log("We have the error", e);
  }
  return idTypes;
};

export const getAllSexTypes = async () => {
  let sexTypes: any[] = [];

  try {
    const response = await BrightMarketApi.getAllSexTypes();
    sexTypes = response.data;
  } catch (e) {
    console.log("We have the error", e);
  }
  return sexTypes;
};

export const getMessageTemplateTypes = async () => {
  try {
    const [statusesResp, langsResp, catsResp] = await Promise.all([
      BrightMarketApi.getMessageTemplateStatuses(),
      BrightMarketApi.getMessageTemplateLanguages(),
      BrightMarketApi.getMessageTemplateCategories(),
    ]);
    return {
      statuses: statusesResp?.data || [],
      languages: langsResp?.data || [],
      categories: catsResp?.data || [],
    };
  } catch (e) {
    console.log("Error fetching message template types:", e);
    return { statuses: [], languages: [], categories: [] };
  }
};

export const getAllCountries = async () => {
  let countries: any[] = [];

  try {
    const response = await BrightMarketApi.getAllCountries();
    countries = response.countries;
  } catch (e) {
    console.log("We have the error", e);
  }
  return countries;
};

export const getAllCountriesWithExistingClients = async () => {
  let countries: any[] = [];
  const organizationId = await getOrganizationId();

  try {
    const response = await BrightMarketApi.getAllCountriesWithExistingClients(
      organizationId
    );
    countries = response.countries;
  } catch (e) {
    console.log("We have the error", e);
  }
  return countries;
};

export const getAllCitiesWithExistingClients = async () => {
  let cities: any[] = [];
  const organizationId = await getOrganizationId();

  try {
    const response = await BrightMarketApi.getAllCitiesWithExistingClients(
      organizationId
    );
    cities = response.states;
  } catch (e) {
    console.log("We have the error", e);
  }
  return cities;
};

export const getAllStatesByCountry = async (countryId: number) => {
  let countries: any[] = [];

  try {
    const response = await BrightMarketApi.getAllStatesByCountry(countryId);
    countries = response.states;
  } catch (e) {
    console.log("We have the error", e);
  }
  return countries;
};

export const getTickets = async (
  pagination: number,
  filters: {
    code?: string;
    branchOfficeId?: number;
    clusterId?: number;
    startDate?: string;
    endDate?: string;
    skuIds?: number[];
    minTotal?: number;
    maxTotal?: number;
    documentType?: string;
    clientId?: number;
  }
) => {
  let tickets: any[] = [];
  let totalElements = 0;

  try {
    const organizationId = await getOrganizationId();

    const response = await BrightMarketApi.getTickets(
      organizationId,
      pagination,
      filters
    );

    tickets = response.tickets.tickets;
    totalElements = response.totalElements;
  } catch (e) {
    console.log("We have the error", e);
  }
  return { tickets, totalElements };
};

export const getTicketsByClientId = async (
  pagination: number,
  clientId: string,
  clusterId?: number
) => {
  let tickets: any[] = [];
  let totalElements = 0;

  try {
    const organizationId = await getOrganizationId();

    const response = await BrightMarketApi.getTicketsByClientId(
      organizationId,
      pagination,
      clientId,
      clusterId
    );

    tickets = response.tickets.tickets;
    totalElements = response.totalElements;
  } catch (e) {
    console.log("We have the error", e);
  }
  return { tickets, totalElements };
};

export const getTicketById = async (ticketId: string) => {
  let ret;
  try {
    const response = await BrightMarketApi.getTicketById(ticketId);
    ret = response.data;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const deleteTicket = async (promotionId: number) => {
  let ret = false;

  try {
    let response = await BrightMarketApi.deleteTicket(promotionId);
    ret = response.status === 204;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const getAllTicketDocumentTypes = async () => {
  let documentTypes: any[] = [];

  try {
    const response = await BrightMarketApi.getAllTicketDocumentTypes();
    documentTypes = response.data;
  } catch (e) {
    console.log("We have the error", e);
  }
  return documentTypes;
};

export const createTicket = async (ticketInfo: any) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.createTicket(ticketInfo);
    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const schedulePromotion = async (
  id: number,
  startDate: any,
  endDate: any
) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.schedulePromotion(
      id,
      startDate,
      endDate
    );
    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const getAllRecurrencesTypes = async () => {
  let recurrencesTypes: any[] = [];

  try {
    const response = await BrightMarketApi.getAllRecurrencesTypes();
    recurrencesTypes = response.data;
  } catch (e) {
    console.log("We have the error", e);
  }
  return recurrencesTypes;
};

export const getPricings = async (
  pagination: number,
  filters: {
    categoryId?: string;
    brandId?: number;
    branchOfficeId?: number;
    articleId?: string;
    articleGroupId?: number;
  }
) => {
  let pricings: any[] = [];
  let totalElements = 0;

  try {
    const organizationId = await getOrganizationId();

    const response = await BrightMarketApi.getPricings(
      organizationId,
      pagination,
      filters
    );

    pricings = response.pricings.pricings;
    totalElements = response.totalElements;
  } catch (e) {
    console.log("We have the error", e);
  }
  return { pricings, totalElements };
};

export const getHistoricPricings = async (filters: {
  page: number;
  articleId?: number;
  brandId?: number;
  categoryId?: number;
}) => {
  const organizationId = await getOrganizationId();
  let pricings: any[] = [];
  let totalElements = 0;

  try {
    const response = await BrightMarketApi.getHistoricPricings(
      organizationId,
      filters
    );
    pricings = response.pricings;
    totalElements = response.totalElements;
  } catch (e) {
    console.log("We have the error", e);
  }

  return { pricings, totalElements };
};

export const deletePricing = async (pricingId: number) => {
  let ret = false;

  try {
    let response = await BrightMarketApi.deletePricing(pricingId);
    ret = response.status === 204;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const getPricingsByMonth = async (month: string) => {
  let promotions: any[] = [];

  try {
    const response = await BrightMarketApi.getPricingsByMonth(month);

    promotions = response.data;
  } catch (e) {
    console.log("We have the error", e);
  }
  return promotions;
};

export const schedulePricing = async (
  id: number,
  startDate: any,
  endDate: any
) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.schedulePricing(
      id,
      startDate,
      endDate
    );
    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const getAllPricingTypes = async () => {
  let types: any[] = [];

  try {
    const response = await BrightMarketApi.getAllPricingTypes();
    types = response.data;
  } catch (e) {
    console.log("We have the error", e);
  }
  return types;
};

export const getAllEndPricingTypes = async () => {
  let types: any[] = [];

  try {
    const response = await BrightMarketApi.getAllEndPricingTypes();
    types = response.data;
  } catch (e) {
    console.log("We have the error", e);
  }
  return types;
};

export const createPricing = async (pricing: any) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.createPricing(pricing);
    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const getPricingsNoDate = async (pagination: number) => {
  let pricings: any[] = [];
  let totalElements = 0;

  try {
    const organizationId = await getOrganizationId();

    const response = await BrightMarketApi.getPricingsNoDate(
      organizationId,
      pagination
    );

    pricings = response.pricings.pricings;
    totalElements = response.totalElements;
  } catch (e) {
    console.log("We have the error", e);
  }
  return { pricings, totalElements };
};

export const getPricingById = async (pricingId: string) => {
  let ret;
  try {
    const response = await BrightMarketApi.getPricingById(pricingId);
    ret = response.data;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const getPricingTimeLineById = async (pricingId: string) => {
  let ret;
  try {
    const response = await BrightMarketApi.getPricingTimeLineById(pricingId);
    ret = response.data;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const editPricing = async (id: number, pricing: any) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.editPricing(id, pricing);
    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const getSupplierPaymentMethods = async (pagination: number) => {
  const supplierPaymentMethods: any[] = [];
  let totalElements = 0;

  try {
    const organizationId = await getOrganizationId();

    const response = await BrightMarketApi.getSupplierPaymentMethods(
      organizationId,
      pagination
    );

    let items = response.supplierPaymentMethods;
    totalElements = response.totalElements;

    items.supplierPaymentMethods.forEach(function (supplierPaymentMethod: any) {
      supplierPaymentMethods.push(supplierPaymentMethod);
    });
  } catch (e) {
    console.log("We have the error", e);
  }
  return { supplierPaymentMethods, totalElements };
};

export const createSupplierPaymentMethod = async (paymentMethod: {
  name: string;
  paymentDeadlineDays: number;
  typeId?: number;
}) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.createSupplierPaymentMethod(
      paymentMethod
    );

    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const deleteSupplierPaymentMethod = async (id: number) => {
  let ret = false;

  try {
    let response = await BrightMarketApi.deleteSupplierPaymentMethod(id);
    ret = response.status === 204;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const editSupplierPaymentMethod = async (
  id: number,
  name: string,
  paymentDeadlineDays: number,
  typeId?: number
) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.editSupplierPaymentMethod(
      id,
      name,
      paymentDeadlineDays,
      typeId
    );
    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const getAllSuppliersPaymentMethods = async () => {
  let supplierPaymentMethods: any[] = [];

  try {
    const response = await BrightMarketApi.getAllSupplierPaymentMethods();
    const items = response.data;
    supplierPaymentMethods = items;
  } catch (e) {
    console.log("We have the error", e);
  }
  return supplierPaymentMethods;
};

export const getPurchaseUnits = async (pagination: number) => {
  const purchaseUnits: any[] = [];
  let totalElements = 0;

  try {
    const organizationId = await getOrganizationId();

    const response = await BrightMarketApi.getPurchaseUnits(
      organizationId,
      pagination
    );

    let items = response.purchaseUnits;
    totalElements = response.totalElements;

    items.purchaseUnits.forEach(function (purchaseUnit: any) {
      purchaseUnits.push(purchaseUnit);
    });
  } catch (e) {
    console.log("We have the error", e);
  }
  return { purchaseUnits, totalElements };
};

export const createPurchaseUnit = async (
  purchaseUnit: string,
  conversionFactor: string
) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.createPurchaseUnit(
      purchaseUnit,
      conversionFactor
    );

    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const deletePurchaseUnit = async (purchaseUnitId: number) => {
  let ret = false;

  try {
    let response = await BrightMarketApi.deletePurchaseUnit(purchaseUnitId);
    ret = response.status === 204;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const editPurchaseUnit = async (
  purchaseUnitId: number,
  purchaseUnit: string,
  conversionFactor: string
) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.editPurchaseUnit(
      purchaseUnitId,
      purchaseUnit,
      conversionFactor
    );

    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const discontinueArticle = async (articleId: number) => {
  let ret = false;

  try {
    let response = await BrightMarketApi.discontinueArticle(articleId);
    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const discontinueBranchOfficeArticle = async (
  branchOfficeId: number,
  articleId: number
) => {
  let ret = false;

  try {
    let response = await BrightMarketApi.discontinueBranchOfficeArticle(
      branchOfficeId,
      articleId
    );
    ret = response.status === 200;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const getExcelTemplate = async (type: string) => {
  let ret: { success: boolean; data?: any; filename?: string } = {
    success: false,
  };

  try {
    let response = await BrightMarketApi.getExcelTemplate(type);

    ret = {
      success: response.status === 200,
      data: response.data,
      filename: response.headers["content-disposition"]
        .split('filename="')[1]
        .split('"')[0],
    };
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const processExcelTemplate = async (type: string, file: any) => {
  let ret: { success: boolean; data?: any; filename?: string } = {
    success: false,
  };

  try {
    let response = await BrightMarketApi.processExcelTemplate(type, file);
    console.log(response);
    ret = {
      success: response.status === 200 && response.data.data.added > 0,
      data: response.data.data,
    };
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const getNotifications = async (pagination: number) => {
  let notifications: any[] = [];
  let totalElements = 0;

  try {
    const response = await BrightMarketApi.getNotifications(pagination);

    notifications = response.notifications;
    totalElements = response.totalElements;
  } catch (e) {
    console.log("We have the error", e);
  }
  return { notifications, totalElements };
};

export const readNotification = async (notificationId: number) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.readNotification(notificationId);

    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const unreadNotification = async (notificationId: number) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.unreadNotification(notificationId);

    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const getSeasons = async (pagination: number) => {
  let seasons: any[] = [];
  let totalElements = 0;

  const organizationId = await getOrganizationId();

  try {
    const response = await BrightMarketApi.getSeasons(
      pagination,
      organizationId
    );

    seasons = response.seasons;
    totalElements = response.totalElements;
  } catch (e) {
    console.log("We have the error", e);
  }
  return { seasons, totalElements };
};

export const getSeasonsFiltered = async (name: string) => {
  let categories = [];

  try {
    const response = await BrightMarketApi.getSeasonsFiltered(name);
    categories = response.data.content;
  } catch (e) {
    console.log("We have the error", e);
  }
  return categories;
};

export const getSeasonById = async (seasonId: string) => {
  let ret;
  try {
    const response = await BrightMarketApi.getSeasonById(seasonId);
    ret = response.data;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};
export const editSeason = async (
  seasonId: string,
  name: string,
  startingDate: string,
  endingDate: string
) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.editSeason(
      seasonId,
      name,
      startingDate,
      endingDate
    );

    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const createSeason = async (
  name: string,
  startingDate: string,
  endingDate: string
) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.createSeason(
      name,
      startingDate,
      endingDate
    );

    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const deleteSeason = async (id: number) => {
  let ret = false;

  try {
    let response = await BrightMarketApi.deleteSeason(id);
    ret = response.status === 204;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const getCommercialDates = async (pagination: number) => {
  let commercialDates: any[] = [];
  let totalElements = 0;

  const organizationId = await getOrganizationId();

  try {
    const response = await BrightMarketApi.getCommercialDates(
      pagination,
      organizationId
    );

    commercialDates = response.commercialDates;
    totalElements = response.totalElements;
  } catch (e) {
    console.log("We have the error", e);
  }
  return { commercialDates, totalElements };
};

export const createCommercialDates = async (
  name: string,
  startingDate: string,
  endingDate: string,
  channelIds?: number[]
) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.createCommercialDates(
      name,
      startingDate,
      endingDate,
      channelIds
    );

    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const getCommercialDateById = async (commercialDateId: string) => {
  let ret;
  try {
    const response = await BrightMarketApi.getCommercialDateById(
      commercialDateId
    );
    ret = response.data;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const editCommercialDate = async (
  commercialDateId: string,
  name: string,
  channelIds?: number[]
) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.editCommercialDate(
      commercialDateId,
      name,
      channelIds
    );

    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const deleteCommercialDate = async (id: number) => {
  let ret = false;

  try {
    let response = await BrightMarketApi.deleteCommercialDate(id);
    ret = response.status === 204;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const getCommercialDateEditionsForYear = async (year: number) => {
  let editions: any[] = [];
  try {
    const response = await BrightMarketApi.getCommercialDateEditionsForYear(
      year
    );
    editions = response.data;
  } catch (e) {
    console.log("We have the error", e);
  }
  return editions;
};

export const getCommercialDateEditions = async (commercialDateId: number) => {
  let editions: any[] = [];
  try {
    const response = await BrightMarketApi.getCommercialDateEditions(
      commercialDateId
    );
    editions = response.data;
  } catch (e) {
    console.log("We have the error", e);
  }
  return editions;
};

export const addCommercialDateEdition = async (
  commercialDateId: number,
  year: number,
  startingDate: string,
  endingDate: string
) => {
  try {
    const response = await BrightMarketApi.addCommercialDateEdition(
      commercialDateId,
      year,
      startingDate,
      endingDate
    );
    return { success: response.data.status === StatusResponse.Ok };
  } catch (e: any) {
    console.log("We have the error", e);
    const apiError = e?.response?.data?.apierror;
    return {
      success: false,
      message: apiError?.message_ES ?? apiError?.message,
    };
  }
};

export const editCommercialDateEdition = async (
  commercialDateId: number,
  editionId: number,
  startingDate: string,
  endingDate: string
) => {
  try {
    const response = await BrightMarketApi.editCommercialDateEdition(
      commercialDateId,
      editionId,
      startingDate,
      endingDate
    );
    return { success: response.data.status === StatusResponse.Ok };
  } catch (e: any) {
    console.log("We have the error", e);
    const apiError = e?.response?.data?.apierror;
    return {
      success: false,
      message: apiError?.message_ES ?? apiError?.message,
    };
  }
};

export const deleteCommercialDateEdition = async (
  commercialDateId: number,
  editionId: number
) => {
  let ret = false;
  try {
    const response = await BrightMarketApi.deleteCommercialDateEdition(
      commercialDateId,
      editionId
    );
    ret = response.status === 204;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const getStockMovements = async (filters: {
  pagination: number;
  businessUnitId: string;
  startDate?: string | null;
  endDate?: string | null;
  articleId?: string | null;
  type?: string | null;
  branchOfficeId?: string;
  depositId?: string;
}) => {
  let stockMovements: any[] = [];
  let totalElements = 0;

  try {
    const organizationId = await getOrganizationId();

    const response = await BrightMarketApi.getStockMovements(
      organizationId,
      filters
    );

    totalElements = response.totalElements;
    stockMovements = response.stockMovements;
  } catch (e) {
    console.log("We have the error", e);
  }

  return { stockMovements, totalElements };
};

export const getMovementStockTypes = async () => {
  let types: any[] = [];

  try {
    const response = await BrightMarketApi.getMovementStockTypes();
    types = response.data;
  } catch (e) {
    console.log("We have the error", e);
  }
  return types;
};

export const createStockMovements = async (movement: {
  skuBranchOfficeId?: number;
  skuDepositId?: number;
  stock: number;
  reason: string;
  date: string;
  type: string;
}) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.createStockMovements(movement);

    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const createColour = async (name: string) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.createColour(name);

    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const getColours = async (
  pagination: number,
  filters: { name: string }
) => {
  let colours: any[] = [];
  let totalElements = 0;

  try {
    const organizationId = await getOrganizationId();

    const response = await BrightMarketApi.getColours(
      organizationId,
      pagination,
      filters
    );

    colours = response.colours;
    totalElements = response.totalElements;
  } catch (e) {
    console.log("We have the error", e);
  }
  return { colours, totalElements };
};

export const getColoursByName = async (name: string) => {
  let totalElements = 0;
  let colours = [];

  try {
    const organizationId = await getOrganizationId();

    const response = await BrightMarketApi.getColoursByName(
      name,
      organizationId
    );

    colours = response.colours;
    totalElements = response.totalElements;
  } catch (e) {
    console.log("We have the error", e);
  }
  return { colours, totalElements };
};

export const deleteColour = async (id: number) => {
  let ret = false;

  try {
    let response = await BrightMarketApi.deleteColour(id);
    ret = response.status === 204;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const editColour = async (id: number, name: string) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.editColour(id, name);

    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const editCluster = async (id: number, name: string) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.editCluster(id, {
      name,
    });

    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const getClusters = async (pagination: number) => {
  let clusters: any[] = [];
  let totalElements = 0;

  try {
    const organizationId = await getOrganizationId();

    const response = await BrightMarketApi.getClusters(
      organizationId,
      pagination
    );

    clusters = response.clusters;
    totalElements = response.totalElements;
  } catch (e) {
    console.log("We have the error", e);
  }
  return { clusters, totalElements };
};

export const addDepositToCluster = async (
  clusterId: number,
  depositId: number
) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.addDepositToCluster(
      clusterId,
      depositId
    );

    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};
export const addBranchOfficeToCluster = async (
  clusterId: number,
  branchOfficeId: number
) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.addBranchOfficeToCluster(
      clusterId,
      branchOfficeId
    );

    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const deletePlaceFromCluster = async (placeId: number) => {
  let ret = false;

  try {
    let response = await BrightMarketApi.deletePlaceFromCluster(placeId);
    ret = response.status === 204;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const createCluster = async (cluster: {
  name: string;
  depositsIds: any;
  branchOfficesIds: any;
}) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.createCluster(cluster);

    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const deleteCluster = async (id: number) => {
  let ret = false;

  try {
    let response = await BrightMarketApi.deleteCluster(id);
    ret = response.status === 204;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const getClusterById = async (id: string) => {
  let ret;
  try {
    const response = await BrightMarketApi.getClusterById(id);
    ret = response.data;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const getVehicles = async (
  pagination: number,
  filters: { name?: string },
  projection?: string
) => {
  let vehicles: any[] = [];
  let totalElements = 0;

  try {
    const organizationId = await getOrganizationId();

    const response = await BrightMarketApi.getVehicles(
      organizationId,
      pagination,
      filters,
      projection
    );

    vehicles = response.vehicles;
    totalElements = response.totalElements;
  } catch (e) {
    console.log("We have the error", e);
  }
  return { vehicles, totalElements };
};

export const deleteVehicle = async (id: number) => {
  let ret = false;

  try {
    let response = await BrightMarketApi.deleteVehicle(id);
    ret = response.status === 204;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const createVehicle = async (vehicle: {
  name: string;
  code: string;
  brand: string;
  model: string;
  licensePlate: string;
  type: string;
  maxCapacity: number;
  fuelConsumption: number;
}) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.createVehicle(vehicle);

    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const getVehicleById = async (id: string) => {
  let ret;
  try {
    const response = await BrightMarketApi.getVehicleById(id);
    ret = response.data;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const deleteDepositFromVehicle = async (id: number) => {
  let ret = false;

  try {
    let response = await BrightMarketApi.deleteDepositFromVehicle(id);
    ret = response.status === 204;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const deleteBranchOfficeFromVehicle = async (id: number) => {
  let ret = false;

  try {
    let response = await BrightMarketApi.deleteBranchOfficeFromVehicle(id);
    ret = response.status === 204;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const deleteScheduleFromVehicle = async (
  vehicleId: string,
  scheduleId: number
) => {
  let ret = false;

  try {
    let response = await BrightMarketApi.deleteScheduleFromVehicle(
      vehicleId,
      scheduleId
    );
    ret = response.status === 204;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const addDepositToVehicle = async (
  vehicleId: number,
  depositId: number
) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.addDepositToVehicle(
      vehicleId,
      depositId
    );

    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const addBranchOfficeToVehicle = async (
  vehicleId: number,
  branchOfficeId: number
) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.addBranchOfficeToVehicle(
      vehicleId,
      branchOfficeId
    );

    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const addScheduleToVehicle = async (schedule: {
  vehicleId: number;
  code: number;
  description: string;
  branchOfficeIdFrom: number | null;
  branchOfficeIdTo: number | null;
  depositIdFrom: number | null;
  depositIdTo: number | null;
  recurrence: any;
}) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.addScheduleToVehicle(schedule);

    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const addScheduleToBranchOffice = async (
  branchOfficeId: number,
  schedule: {
    code: number;
    description: string;
    branchOfficeIdTo: number | null;
    depositIdTo: number | null;
    vehicleId: number;
    recurrence: any;
  }
) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.addScheduleToBranchOffice(
      branchOfficeId,
      schedule
    );

    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const addScheduleToDeposit = async (
  depositId: number,
  schedule: {
    code: number;
    description: string;
    branchOfficeIdTo: number | null;
    depositIdTo: number | null;
    vehicleId: number;
    recurrence: any;
  }
) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.addScheduleToDeposit(
      depositId,
      schedule
    );

    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const deleteScheduleFromDeposit = async (
  depositId: number,
  scheduleId: number
) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.deleteScheduleFromDeposit(
      depositId,
      scheduleId
    );
    ret = response.status === 204;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const deleteScheduleFromBranchOffice = async (
  branchOfficeId: number,
  scheduleId: number
) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.deleteScheduleFromBranchOffice(
      branchOfficeId,
      scheduleId
    );
    ret = response.status === 204;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const editVehicle = async (
  id: number,
  vehicle: {
    name: string;
    code: string;
    brand: string;
    model: string;
    licensePlate: string;
    type: string;
    maxCapacity: string;
    fuelConsumption: string;
  }
) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.editVehicle(id, vehicle);

    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const createCurve = async (curve: {
  name: string;
  sizeStart: number;
  sizeEnd: number[];
  distribution: number[];
}) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.createCurve(curve);

    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const getCurves = async (
  pagination: number,
  filters: { name?: string }
) => {
  let curves: any[] = [];
  let totalElements = 0;

  try {
    const organizationId = await getOrganizationId();

    const response = await BrightMarketApi.getCurves(
      organizationId,
      pagination,
      filters
    );

    curves = response.curves;
    totalElements = response.totalElements;
  } catch (e) {
    console.log("We have the error", e);
  }
  return { curves, totalElements };
};

export const deleteCurve = async (id: number) => {
  let ret = false;

  try {
    let response = await BrightMarketApi.deleteCurve(id);
    ret = response.status === 204;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const getCurveById = async (id: string) => {
  let ret;
  try {
    const response = await BrightMarketApi.getCurveById(id);
    ret = response.data;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const editCurve = async (
  id: number,
  curve: {
    name: string;
    sizeStart: number;
    sizeEnd: number[];
    distribution: number[];
  }
) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.editCurve(id, curve);

    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const addCurveToArticle = async (articleId: number, curveId: number) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.addCurveToArticle(articleId, curveId);

    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const deleteCurveFromArticle = async (
  articleId: number,
  curveId: number
) => {
  let ret = false;

  try {
    let response = await BrightMarketApi.deleteCurveFromArticle(
      articleId,
      curveId
    );
    ret = response.status === 204;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const getHistoricalStockBranchOffice = async (
  branchOfficeId: string,
  pagination: number,
  filters: { startDate?: string; endDate?: string }
) => {
  let stocks: any[] = [];
  let totalElements = 0;

  try {
    const organizationId = await getOrganizationId();

    const response = await BrightMarketApi.getHistoricalStockBranchOffice(
      organizationId,
      branchOfficeId,
      pagination,
      filters
    );

    stocks = response.stocks;
    totalElements = response.totalElements;
  } catch (e) {
    console.log("We have the error", e);
  }
  return { stocks, totalElements };
};

export const getHistoricalStockDeposit = async (
  depositId: string,
  pagination: number,
  filters: { startDate?: string; endDate?: string }
) => {
  let stocks: any[] = [];
  let totalElements = 0;

  try {
    const organizationId = await getOrganizationId();

    const response = await BrightMarketApi.getHistoricalStockDeposit(
      organizationId,
      depositId,
      pagination,
      filters
    );

    stocks = response.stocks;
    totalElements = response.totalElements;
  } catch (e) {
    console.log("We have the error", e);
  }
  return { stocks, totalElements };
};

export const ruleCreateRepositionOrdersForStockMin = async () => {
  let ret = false;
  try {
    let response =
      await BrightMarketApi.ruleCreateRepositionOrdersForStockMin();

    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const ruleCreateRepositionOrdersForStock0 = async () => {
  let ret = false;
  try {
    let response = await BrightMarketApi.ruleCreateRepositionOrdersForStock0();

    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const getClientLevels = async (
  pagination: number,
  filters: { name?: string; level?: string; origin?: string }
) => {
  let clientLevels: any[] = [];
  let totalElements = 0;

  try {
    const organizationId = await getOrganizationId();

    const response = await BrightMarketApi.getClientLevels(
      organizationId,
      pagination,
      filters
    );

    clientLevels = response.clientLevels;
    totalElements = response.totalElements;
  } catch (e) {
    console.log("We have the error", e);
  }
  return { clientLevels, totalElements };
};

export const getClientLevelsForList = async (
  pagination: number,
  filters: { name?: string; level?: string; origin?: string }
) => {
  let clientLevels: any[] = [];
  let totalElements = 0;

  try {
    const organizationId = await getOrganizationId();

    const response = await BrightMarketApi.getClientLevelsForList(
      organizationId,
      pagination,
      filters
    );

    clientLevels = response.clientLevels;
    totalElements = response.totalElements;
  } catch (e) {
    console.log("We have the error", e);
  }
  return { clientLevels, totalElements };
};

export const deleteClientLevel = async (id: number) => {
  let ret = false;

  try {
    let response = await BrightMarketApi.deleteClientLevel(id);
    ret = response.status === 204;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const getAccumulationUnits = async (
  pagination: number,
  filters: { name?: string; code?: string }
) => {
  let accumulationUnits: any[] = [];
  let totalElements = 0;

  try {
    const response = await BrightMarketApi.getAccumulationUnits(pagination, filters);
    accumulationUnits = response.accumulationUnits;
    totalElements = response.totalElements;
  } catch (e) {
    console.log("We have the error", e);
  }
  return { accumulationUnits, totalElements };
};

export const deleteAccumulationUnit = async (id: number) => {
  let ret = false;

  try {
    const response = await BrightMarketApi.deleteAccumulationUnit(id);
    ret = response.status === 204;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const createAccumulationUnit = async (unit: {
  name: string;
  code: string;
  baseAccumulationFactor?: number | null;
  specificAccumulationFactor?: number | null;
  accumulationUnitValue?: number | null;
  useAccumulationForPayment: boolean;
  useClientTiers: boolean;
}) => {
  let ret = false;
  try {
    const response = await BrightMarketApi.createAccumulationUnit(unit);
    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const updateAccumulationUnit = async (
  id: number,
  unit: {
    name: string;
    code: string;
    baseAccumulationFactor?: number | null;
    specificAccumulationFactor?: number | null;
    accumulationUnitValue?: number | null;
    useAccumulationForPayment: boolean;
    useClientTiers: boolean;
  }
) => {
  let ret = false;
  try {
    const response = await BrightMarketApi.updateAccumulationUnit(id, unit);
    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const createClientLevel = async (clientLevel: {
  name: string;
  level: number;
  description: string;
  ruleType: string;
  ruleQuantity: number;
  accumulationFactor: number;
  active: boolean;
}) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.createClientLevel(clientLevel);

    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const getClientLevelById = async (id: string) => {
  let ret;
  try {
    const response = await BrightMarketApi.getClientLevelById(id);
    ret = response.data;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const editClientLevel = async (
  id: number,
  clientLevel: {
    name: string;
    level: number;
    description: string;
    ruleType: string;
    ruleQuantity: number;
    accumulationFactor: number;
    active: boolean;
  }
) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.editClientLevel(id, clientLevel);

    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const getArticleGroups = async (
  pagination: number,
  filters: { name?: string }
) => {
  let articleGroups: any[] = [];
  let totalElements = 0;

  try {
    const organizationId = await getOrganizationId();

    const response = await BrightMarketApi.getArticleGroups(
      organizationId,
      pagination,
      filters
    );

    articleGroups = response.articleGroups;
    totalElements = response.totalElements;
  } catch (e) {
    console.log("We have the error", e);
  }
  return { articleGroups, totalElements };
};

export const deleteArticleGroup = async (id: number) => {
  let ret = false;

  try {
    let response = await BrightMarketApi.deleteArticleGroup(id);
    ret = response.status === 204;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const createArticleGroup = async (articleGroup: {
  name: string;
  description: string;
  categoryId: string;
  brandId: string;
  branchOfficeId: string;
  articleIds: string[];
  state: boolean;
}) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.createArticleGroup(articleGroup);

    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const getArticleGroupById = async (id: string) => {
  let ret;
  try {
    const response = await BrightMarketApi.getArticleGroupById(id);
    ret = response.data;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const getArticleGroupCountById = async (id: string) => {
  let ret;
  try {
    const response = await BrightMarketApi.getArticleGroupCountById(id);
    ret = response.data;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const editArticleGroup = async (
  id: number,
  articleGroup: {
    name: string;
    description: string;
    categoryId: string;
    brandId: string;
    branchOfficeId: string;
    articleIds: string[];
    state: boolean;
  }
) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.editArticleGroup(id, articleGroup);

    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const getRewardTypes = async (
  pagination: number,
  filters: { name?: string; active?: number }
) => {
  let rewardTypes: any[] = [];
  let totalElements = 0;

  try {
    const organizationId = await getOrganizationId();

    const response = await BrightMarketApi.getRewardTypes(
      organizationId,
      pagination,
      filters
    );

    rewardTypes = response.rewardTypes;
    totalElements = response.totalElements;
  } catch (e) {
    console.log("We have the error", e);
  }
  return { rewardTypes, totalElements };
};

export const deleteRewardType = async (id: number) => {
  let ret = false;

  try {
    let response = await BrightMarketApi.deleteRewardType(id);
    ret = response.status === 204;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const createRewardType = async (rewardType: {
  name: string;
  status: boolean;
  values?: { value: number; valueType: string }[];
}) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.createRewardType(rewardType);

    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const getRewardTypeById = async (id: string) => {
  let ret;
  try {
    const response = await BrightMarketApi.getRewardTypeById(id);
    ret = response.data;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const editRewardType = async (
  id: string,
  rewardType: {
    name: string;
    status: boolean;
    values?: { value: number; valueType: string }[];
  }
) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.editRewardType(id, rewardType);

    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const getRewards = async (
  pagination: number,
  filters: {
    name?: string;
    active?: boolean;
    dateStart?: string;
    dateEnd?: string;
    type?: number;
    clusterId?: number;
    origin?: string;
  }
) => {
  const organizationId = await getOrganizationId();
  return await BrightMarketApi.getRewards(organizationId, pagination, filters);
};

export const deleteReward = async (id: number) => {
  let ret = false;

  try {
    let response = await BrightMarketApi.deleteReward(id);
    ret = response.status === 204;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const createReward = async (reward: {
  name: string;
  status: boolean;
  rewardType: string;
  description: string;
  value: number;
  cost?: number;
  estimatedReach?: number;
  estimatedImpact?: number;
  estimatedRoi?: number;
  accumulatedImpact?: number;
  accumulatedCost?: number;
  historicalRoi?: number;
  pointsValue: number;
  validityStart: string;
  validityEnd: string;
  codeLength: number;
  codeType: string;
  prefix: string;
  brandIds: any[];
  categoryIds: any[];
  clientSegmentIds: any[];
  clientIds?: any[];
  levelIds: any[];
  articleIds: any[];
  articleGroupIds: any[];
  clusterIds: any[];
  branchOfficeIds: any[];
}) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.createReward(reward);

    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const getRewardClients = async (
  rewardId: string,
  params: {
    page?: number;
    size?: number;
    sortBy?: string;
    direction?: string;
    clientId?: string;
    name?: string;
    email?: string;
    idNumber?: string;
    mobile?: string;
  }
) => {
  let ret;
  try {
    ret = await BrightMarketApi.getRewardClients(rewardId, params);
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const addSegmentClientsToReward = async (
  rewardId: string,
  segmentIds: number[],
  rewardFields?: { name?: string; status?: boolean }
) => {
  try {
    return await BrightMarketApi.addSegmentClientsToReward(
      rewardId,
      segmentIds,
      rewardFields
    );
  } catch (e) {
    console.log("We have the error", e);
    throw e;
  }
};

export const editReward = async (
  id: string,
  reward: {
    name: string;
    status: boolean;
    rewardType: string;
    description: string;
    value: number;
    cost?: number;
    estimatedReach?: number;
    estimatedImpact?: number;
    estimatedRoi?: number;
    accumulatedImpact?: number;
    accumulatedCost?: number;
    historicalRoi?: number;
    pointsValue: number;
    validityStart: string;
    validityEnd: string;
    codeLength: number;
    codeType: string;
    prefix: string;
    brandIds: any[];
    categoryIds: any[];
    replaceClients?: boolean;
    clientSegmentIds?: any[];
    clientIds?: any[];
    addClientIds?: any[];
    removeClientIds?: any[];
    levelIds: any[];
    articleIds: any[];
    articleGroupIds: any[];
    clusterIds?: any[];
    branchOfficeIds?: any[];
  }
) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.editReward(id, reward);

    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};
export const getRewardById = async (id: string) => {
  let ret;
  try {
    const response = await BrightMarketApi.getRewardById(id);
    ret = response.data;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

// Campaigns Services
export const getCampaigns = async (pagination: number) => {
  let campaigns: any[] = [];
  let totalElements = 0;

  try {
    const organizationId = await getOrganizationId();
    const response = await BrightMarketApi.getCampaigns(organizationId, pagination);

    if (response?.data?.status === StatusResponse.Ok) {
      campaigns = response.data.data?.content || [];
      totalElements = response.data.data?.totalElements || 0;
    }
  } catch (e) {
    console.log("Error fetching campaigns:", e);
  }

  return { campaigns, totalElements };
};

export const getCampaignById = async (id: string) => {
  let ret;
  try {
    const response = await BrightMarketApi.getCampaignById(id);
    if (response.data.status === StatusResponse.Ok) {
      ret = response.data.data;
    }
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const getCampaignClientSegmentIds = async (
  campaignId: string | number
): Promise<{ id: number; name: string }[]> => {
  try {
    return await BrightMarketApi.getCampaignClientSegmentIds(campaignId);
  } catch (e) {
    console.log("We have the error", e);
    return [];
  }
};

export const createCampaign = async (campaign: {
  name: string;
  description?: string;
  validityStartDate?: string;
  validityEndDate?: string;
  promotionId?: number;
  rewardId?: number;
  clientSegmentIds?: number[];
  campaignType?: string;
  campaignStatus?: string;
  estimatedImpact?: number;
  estimatedCost?: number;
  estimatedRoi?: number;
  estimatedAudience?: number;
}) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.createCampaign(campaign);
    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const updateCampaign = async (
  id: string,
  campaign: {
    name?: string;
    description?: string;
    validityStartDate?: string;
    validityEndDate?: string;
    promotionId?: number;
    rewardId?: number;
    clientSegmentIds?: number[];
    campaignType?: string;
    campaignStatus?: string;
    estimatedImpact?: number;
    estimatedCost?: number;
    estimatedRoi?: number;
    estimatedAudience?: number;
  }
) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.updateCampaign(id, campaign);
    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const deleteCampaign = async (id: number) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.deleteCampaign(id.toString());
    ret = response.status === 204;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const getCampaignExport = async (campaignId: number) => {
  let ret: { success: boolean; data?: ArrayBuffer; filename?: string } = {
    success: false,
  };
  try {
    const response = await BrightMarketApi.getCampaignExport(campaignId.toString());
    const contentDisposition = response.headers["content-disposition"];
    const filename = contentDisposition
      ? contentDisposition.split('filename="')[1]?.split('"')[0] || `Datos_campana_${campaignId}.xlsx`
      : `Datos_campana_${campaignId}.xlsx`;
    ret = {
      success: response.status === 200,
      data: response.data,
      filename,
    };
  } catch (e) {
    console.log("Error downloading campaign export:", e);
  }
  return ret;
};

export const getCampaignAudienceExport = async (campaignId: number) => {
  let ret: { success: boolean; data?: ArrayBuffer; filename?: string } = {
    success: false,
  };
  try {
    const response = await BrightMarketApi.getCampaignAudienceExport(
      campaignId.toString()
    );
    const data = response.data as ArrayBuffer;
    const contentType = String(response.headers?.["content-type"] || "");
    if (
      response.status !== 200 ||
      !data ||
      contentType.includes("application/json")
    ) {
      return ret;
    }
    const contentDisposition = response.headers?.["content-disposition"];
    const filenameFromHeader = contentDisposition
      ? contentDisposition.split('filename="')[1]?.split('"')[0] ||
        contentDisposition.split("filename=")[1]?.split(";")[0]?.trim()
      : undefined;
    ret = {
      success: true,
      data,
      filename:
        filenameFromHeader || `Audiencia_campana_${campaignId}.csv`,
    };
  } catch (e) {
    console.log("Error downloading campaign audience export:", e);
  }
  return ret;
};

export const getCampaignClients = async (
  campaignId: string,
  page: number,
  options?: {
    type?: "EMAIL" | "SMS" | "WHATSAPP" | null;
    excludeClientIds?: number[];
    size?: number;
    name?: string;
    email?: string;
    phone?: string;
    idNumber?: string;
  }
) => {
  let clients: any[] = [];
  let totalElements = 0;

  try {
    const response = await BrightMarketApi.getCampaignClients(
      campaignId,
      page,
      options
    );

    if (response?.data?.status === StatusResponse.Ok) {
      clients = response.data.data?.content || [];
      totalElements = response.data.data?.totalElements || 0;
    }
  } catch (e) {
    console.log("Error fetching campaign clients:", e);
  }

  return { clients, totalElements };
};

export const getMessageTemplates = async (opts?: {
  refresh?: boolean;
  page?: number;
  size?: number;
  status?: string;
}) => {
  let templates: any[] = [];
  let totalElements = 0;
  try {
    const response = await BrightMarketApi.getMessageTemplates({
      refresh: !!opts?.refresh,
      page: opts?.page ?? 0,
      size: opts?.size ?? 10,
      status: opts?.status,
    });
    if (response?.data?.status === StatusResponse.Ok) {
      templates = response.data.data?.content || [];
      totalElements = response.data.data?.totalElements || 0;
    }
  } catch (e) {
    console.log("Error fetching message templates:", e);
  }
  return { templates, totalElements };
};

export const getMessageTemplate = async (id: number) => {
  try {
    const response = await BrightMarketApi.getMessageTemplate(id);
    if (response?.data?.status === StatusResponse.Ok) {
      return response.data.data;
    }
  } catch (e) {
    console.log("Error fetching message template:", e);
  }
  return null;
};

export const createMessageTemplate = async (payload: {
  name: string;
  language: string; // enum value (e.g. ES/EN)
  category: string; // enum value (e.g. UTILITY/MARKETING/AUTHENTICATION)
  body: string;
  submitForWhatsAppApproval?: boolean;
}) => {
  let ret = false;
  try {
    const response = await BrightMarketApi.createMessageTemplate(payload);
    ret = response?.data?.status === StatusResponse.Ok;
  } catch (e) {
    console.log("Error creating message template:", e);
  }
  return ret;
};

export const updateMessageTemplate = async (
  id: number,
  payload: {
    name: string;
    language: string;
    category: string;
    body: string;
    submitForWhatsAppApproval?: boolean;
  }
) => {
  let ret = false;
  try {
    const response = await BrightMarketApi.updateMessageTemplate(id, payload);
    ret = response?.data?.status === StatusResponse.Ok;
  } catch (e) {
    console.log("Error updating message template:", e);
  }
  return ret;
};

export const submitMessageTemplate = async (id: number) => {
  try {
    const response = await BrightMarketApi.submitMessageTemplate(id);
    if (response?.data?.status === StatusResponse.Ok) {
      return response.data.data; // Return the updated template
    }
  } catch (e) {
    console.log("Error submitting message template:", e);
  }
  return null;
};

export const refreshMessageTemplate = async (id: number) => {
  let ret = false;
  try {
    const response = await BrightMarketApi.refreshMessageTemplate(id);
    ret = response?.data?.status === StatusResponse.Ok;
  } catch (e) {
    console.log("Error refreshing message template:", e);
  }
  return ret;
};

export const deleteMessageTemplate = async (id: number) => {
  let ret = false;
  try {
    const response = await BrightMarketApi.deleteMessageTemplate(id);
    ret = response?.data?.status === StatusResponse.Ok;
  } catch (e) {
    console.log("Error deleting message template:", e);
  }
  return ret;
};

export const sendCampaign = async (
  campaignId: string,
  clientIds: number[],
  type: "EMAIL" | "SMS" | "WHATSAPP",
  scheduledAt?: string,
  messageBody?: string,
  messageTemplateId?: number,
  templateVariables?: string
) => {
  let ret = false;
  try {
    let response = await BrightMarketApi.sendCampaign(
      campaignId,
      clientIds,
      type,
      scheduledAt,
      messageBody,
      messageTemplateId,
      templateVariables
    );
    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const getCampaignSents = async (campaignId: string, page: number) => {
  let sents: any[] = [];
  let totalElements = 0;

  try {
    const response = await BrightMarketApi.getCampaignSents(campaignId, page);
    if (response?.data?.status === StatusResponse.Ok) {
      sents = response.data.data?.content || [];
      totalElements = response.data.data?.totalElements || 0;
    }
  } catch (e) {
    console.log("Error fetching campaign sents:", e);
  }

  return { sents, totalElements };
};

export const getCampaignSentBatches = async (campaignId: string, page: number) => {
  let batches: any[] = [];
  let totalElements = 0;

  try {
    const response = await BrightMarketApi.getCampaignSentBatches(campaignId, page);
    if (response?.data?.status === StatusResponse.Ok) {
      batches = response.data.data?.content || [];
      totalElements = response.data.data?.totalElements || 0;
    }
  } catch (e) {
    console.log("Error fetching campaign sent batches:", e);
  }

  return { batches, totalElements };
};

export const getCampaignSentBatchDetail = async (
  campaignId: string,
  batchId: string,
  options?: { page?: number; size?: number; status?: string; phone?: string; client?: string }
) => {
  try {
    const response = await BrightMarketApi.getCampaignSentBatchDetail(campaignId, batchId, options);
    if (response?.data?.status === StatusResponse.Ok) {
      return response.data.data;
    }
  } catch (e) {
    console.log("Error fetching campaign sent batch detail:", e);
  }
  return null;
};

export const getSupplierCataloguesBySupplierId = async (supplierId: number) => {
  let ret: any[] = [];
  try {
    if (!supplierId) {
      console.warn("Supplier ID is undefined or null");
      return ret;
    }

    const response = await BrightMarketApi.getSupplierCataloguesBySupplierId(
      supplierId
    );

    if (response && Array.isArray(response)) {
      ret = response.map((r: any) => r.data);
    } else {
      console.warn("Unexpected response structure:", response);
      ret = [];
    }
  } catch (e: any) {
    console.error("Error fetching supplier catalogues:", e);
    ret = [];
  }
  return ret;
};

export const createSupplierCatalogue = async (data: any) => {
  let ret = false;
  try {
    const response = await BrightMarketApi.createSupplierCatalogue(data);
    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const editSupplierCatalogue = async (id: number, data: any) => {
  let ret = false;
  try {
    const response = await BrightMarketApi.editSupplierCatalogue(id, data);
    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const deleteSupplierCatalogue = async (id: number) => {
  let ret = false;
  try {
    const response = await BrightMarketApi.deleteSupplierCatalogue(id);
    ret = response.data.status === StatusResponse.Ok;
  } catch (e) {
    console.log("We have the error", e);
  }
  return ret;
};

export const uploadStockMovementFile = async (
  file: File,
  start: boolean = false
) => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("start", start.toString());

  const response = await BrightMarketApi.uploadStockMovementFile(formData);
  return response;
};

export const getStockMovementImports = async (
  pagination: number,
  size?: number
) => {
  const response = await BrightMarketApi.getStockMovementImports(
    pagination,
    size
  );
  return response;
};

export const getStockMovementImportProgress = async (processId: string) => {
  const response = await BrightMarketApi.getStockMovementImportProgress(
    processId
  );
  return response;
};

export const cancelStockMovementImport = async (processId: string) => {
  const response = await BrightMarketApi.cancelStockMovementImport(processId);
  return response;
};

export const deleteStockMovementImport = async (processId: string) => {
  const response = await BrightMarketApi.deleteStockMovementImport(processId);
  return response;
};

export const downloadStockMovementImportErrors = async (processId: string) => {
  const response = await BrightMarketApi.downloadStockMovementImportErrors(
    processId
  );
  return response;
};

export const downloadOriginalStockMovementFile = async (processId: string) => {
  const response = await BrightMarketApi.downloadOriginalStockMovementFile(
    processId
  );
  return response;
};

export const startStockMovementImport = async (processId: string) => {
  const response = await BrightMarketApi.startStockMovementImport(processId);
  return response;
};

export const uploadTicketFile = async (file: File, start: boolean = false) => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("start", start.toString());

  const response = await BrightMarketApi.uploadTicketFile(formData);
  return response;
};

export const getTicketImports = async (pagination: number, size: number) => {
  const response = await BrightMarketApi.getTicketImports(pagination, size);
  return response;
};

export const getTicketImportProgress = async (processId: string) => {
  const response = await BrightMarketApi.getTicketImportProgress(processId);
  return response;
};

export const cancelTicketImport = async (processId: string) => {
  const response = await BrightMarketApi.cancelTicketImport(processId);
  return response;
};

export const deleteTicketImport = async (processId: string) => {
  const response = await BrightMarketApi.deleteTicketImport(processId);
  return response;
};

export const downloadTicketImportErrors = async (processId: string) => {
  const response = await BrightMarketApi.downloadTicketImportErrors(processId);
  return response;
};

export const downloadOriginalTicketFile = async (processId: string) => {
  const response = await BrightMarketApi.downloadOriginalTicketFile(processId);
  return response;
};

export const previewTicketImportJsonl = async (
  processId: string,
  page: number,
  size: number,
  search?: string
) => {
  const response = await BrightMarketApi.previewTicketImportJsonl(
    processId,
    page,
    size,
    search
  );
  return response.data.data;
};

export const startTicketImport = async (processId: string) => {
  const response = await BrightMarketApi.startTicketImport(processId);
  return response;
};

export const uploadRewardFile = async (file: File, start: boolean = false) => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("start", start.toString());

  const response = await BrightMarketApi.uploadRewardFile(formData);
  return response;
};

export const getRewardImports = async (pagination: number, size: number) => {
  const response = await BrightMarketApi.getRewardImports(pagination, size);
  return response;
};

export const getRewardImportProgress = async (processId: string) => {
  const response = await BrightMarketApi.getRewardImportProgress(processId);
  return response;
};

export const cancelRewardImport = async (processId: string) => {
  const response = await BrightMarketApi.cancelRewardImport(processId);
  return response;
};

export const deleteRewardImport = async (processId: string) => {
  const response = await BrightMarketApi.deleteRewardImport(processId);
  return response;
};

export const downloadRewardImportErrors = async (processId: string) => {
  const response = await BrightMarketApi.downloadRewardImportErrors(processId);
  return response;
};

export const downloadOriginalRewardFile = async (processId: string) => {
  const response = await BrightMarketApi.downloadOriginalRewardFile(processId);
  return response;
};

export const startRewardImport = async (processId: string) => {
  const response = await BrightMarketApi.startRewardImport(processId);
  return response;
};

export const uploadPricingFile = async (file: File, start: boolean = false) => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("start", start.toString());
  const response = await BrightMarketApi.uploadPricingFile(formData);
  return response;
};

export const getPricingImports = async (pagination: number, size: number) => {
  const response = await BrightMarketApi.getPricingImports(pagination, size);
  return response;
};

export const getPricingImportProgress = async (processId: string) => {
  const response = await BrightMarketApi.getPricingImportProgress(processId);
  return response;
};

export const cancelPricingImport = async (processId: string) => {
  const response = await BrightMarketApi.cancelPricingImport(processId);
  return response;
};

export const deletePricingImport = async (processId: string) => {
  const response = await BrightMarketApi.deletePricingImport(processId);
  return response;
};

export const downloadPricingImportErrors = async (processId: string) => {
  const response = await BrightMarketApi.downloadPricingImportErrors(processId);
  return response;
};

export const downloadOriginalPricingFile = async (processId: string) => {
  const response = await BrightMarketApi.downloadOriginalPricingFile(processId);
  return response;
};

export const startPricingImport = async (processId: string) => {
  const response = await BrightMarketApi.startPricingImport(processId);
  return response;
};

// --- Activation Actions ---

export const getActivationActions = async (
  pagination: number,
  filters: {
    name?: string;
    status?: string;
    size?: number;
  }
) => {
  let activationActions: any[] = [];
  let totalElements = 0;

  try {
    const response = await BrightMarketApi.getActivationActions(pagination, filters);
    activationActions = response.activationActions ?? [];
    totalElements = response.totalElements ?? 0;
  } catch (e) {
    console.log("Error fetching activation actions", e);
  }
  return { activationActions, totalElements };
};

export const getActivationAction = async (id: number) => {
  try {
    const response = await BrightMarketApi.getActivationAction(id);
    return response;
  } catch (e) {
    console.log("Error fetching activation action", e);
    return null;
  }
};

export const getActivationActionClients = async (
  id: number,
  page: number,
  size: number
) => {
  try {
    const response = await BrightMarketApi.getActivationActionClients(id, page, size);
    const clients = response?.clients ?? [];
    const totalElements = response?.totalElements ?? 0;
    return { clients, totalElements };
  } catch (e) {
    console.log("Error fetching activation action clients", e);
    return { clients: [], totalElements: 0 };
  }
};

export const convertActivationActionToCampaign = async (id: number) => {
  try {
    const response = await BrightMarketApi.convertActivationActionToCampaign(id);
    return { success: true as const, data: response };
  } catch (e: any) {
    console.log("Error converting activation action to campaign", e);
    const apiError = e?.response?.data?.apierror;
    return {
      success: false as const,
      data: null,
      message:
        apiError?.message_ES ??
        apiError?.message ??
        e?.response?.data?.message_ES ??
        e?.response?.data?.message ??
        null,
      timedOut: !e?.response && (e?.code === "ECONNABORTED" || e?.message?.includes("timeout")),
    };
  }
};

export const deleteActivationAction = async (id: number) => {
  let ret = false;
  try {
    const response = await BrightMarketApi.deleteActivationAction(id);
    ret = response.status === 200;
  } catch (e) {
    console.log("Error deleting activation action", e);
  }
  return ret;
};

export const runActivationEngine = async () => {
  try {
    const response = await BrightMarketApi.runActivationEngine();
    return response;
  } catch (e) {
    console.log("Error running activation engine", e);
    return null;
  }
};

// --- Organization Parameters (Activation Actions) ---

export const getAverageTicketBuckets = async () => {
  return BrightMarketApi.getAverageTicketBuckets();
};

export const replaceAverageTicketBuckets = async (body: {
  buckets: Array<{
    min: number | null;
    max: number | null;
    typeValues: Array<{
      rewardTypeId: number;
      minValue: number | null;
      maxValue: number | null;
    }>;
  }>;
}) => {
  return BrightMarketApi.replaceAverageTicketBuckets(body);
};

export const getOrganizationParameters = async () => {
  try {
    const response = await BrightMarketApi.getOrganizationParameters();
    return Array.isArray(response) ? response : [];
  } catch (e) {
    console.log("Error fetching organization parameters", e);
    return [];
  }
};

export const createOrUpdateOrganizationParameter = async (
  paramKey: string,
  paramValue: string,
  description?: string
) => {
  try {
    const response = await BrightMarketApi.createOrUpdateOrganizationParameter(
      paramKey,
      paramValue,
      description
    );
    return response;
  } catch (e) {
    console.log("Error saving organization parameter", e);
    throw e;
  }
};

// --- Product Analysis ---

export const getProductAnalysisLanding = async (params: {
  search?: string;
  categoryId?: number;
  seasonId?: number;
  brandId?: number;
  branchOfficeId?: number;
  supplierExternalId?: string;
  period?: number;
  stockFilter?: string;
  sortBy?: string;
  sortOrder?: string;
  page?: number;
  size?: number;
}): Promise<ProductAnalysisLandingPage> => {
  try {
    const response = await BrightMarketApi.getProductAnalysisLanding(params);
    return response?.data ?? { content: [], totalElements: 0, totalPages: 0, number: 0, size: 0 };
  } catch (e) {
    console.log("Error fetching product analysis landing", e);
    return { content: [], totalElements: 0, totalPages: 0, number: 0, size: 0 };
  }
};


export const getProductAnalysisHeader = async (
  id: number
): Promise<ProductAnalysisHeader | null> => {
  try {
    const response = await BrightMarketApi.getProductAnalysisHeader(id);
    return response?.data ?? null;
  } catch (e) {
    console.log("Error fetching product analysis header", e);
    return null;
  }
};

export const getProductAnalysisKpis = async (
  id: number,
  params: { period?: number; channelId?: number; branchOfficeId?: number } = {}
): Promise<ProductAnalysisKpis | null> => {
  try {
    const response = await BrightMarketApi.getProductAnalysisKpis(id, params);
    return response?.data ?? null;
  } catch (e) {
    console.log("Error fetching product analysis kpis", e);
    return null;
  }
};

export const getProductAnalysisDetailBody = async (
  id: number,
  params: { period?: number; channelId?: number; branchOfficeId?: number } = {}
): Promise<ProductAnalysisDetailBody | null> => {
  try {
    const response = await BrightMarketApi.getProductAnalysisDetailBody(id, params);
    return response?.data ?? null;
  } catch (e) {
    console.log("Error fetching product analysis detail body", e);
    return null;
  }
};

export const getProductAnalysisDetailChart = async (
  id: number,
  params: { period?: number; channelId?: number; branchOfficeId?: number } = {}
): Promise<ProductAnalysisChart | null> => {
  try {
    const response = await BrightMarketApi.getProductAnalysisDetailChart(id, params);
    return response?.data ?? null;
  } catch (e) {
    console.log("Error fetching product analysis detail chart", e);
    return null;
  }
};

export const getProductAnalysisDetailBreakdown = async (
  id: number,
  params: { period?: number; channelId?: number; branchOfficeId?: number } = {}
): Promise<ProductAnalysisBreakdown | null> => {
  try {
    const response = await BrightMarketApi.getProductAnalysisDetailBreakdown(id, params);
    return response?.data ?? null;
  } catch (e) {
    console.log("Error fetching product analysis detail breakdown", e);
    return null;
  }
};

export const getProductSimilarArticles = async (
  skuId: number
): Promise<ProductSimilarArticles | null> => {
  try {
    const response = await BrightMarketApi.getProductSimilarArticles(skuId);
    return response?.data ?? null;
  } catch (e) {
    console.log("Error fetching product similar articles", e);
    return null;
  }
};

export const getBrandAnalysisLanding = async (params: {
  search?: string;
  period?: number;
  channelId?: number;
  branchOfficeId?: number;
  sortBy?: string;
  sortOrder?: string;
}): Promise<BrandAnalysisLanding> => {
  try {
    const response = await BrightMarketApi.getBrandAnalysisLanding(params);
    return (
      response?.data ?? {
        brands: [],
        dependency: { top1: null, top3: null, top5: null },
        potentialBrands: [],
      }
    );
  } catch (e) {
    console.log("Error fetching brand analysis landing", e);
    return {
      brands: [],
      dependency: { top1: null, top3: null, top5: null },
      potentialBrands: [],
    };
  }
};

export const getBrandAnalysisDetail = async (
  id: number,
  params: { period?: number; channelId?: number; branchOfficeId?: number } = {}
): Promise<BrandAnalysisDetail | null> => {
  try {
    const response = await BrightMarketApi.getBrandAnalysisDetail(id, params);
    return response?.data ?? null;
  } catch (e) {
    console.log("Error fetching brand analysis detail", e);
    return null;
  }
};

export const getBrandAnalysisDetailCore = async (
  id: number,
  params: { period?: number; channelId?: number; branchOfficeId?: number } = {}
): Promise<BrandAnalysisDetailCore | null> => {
  try {
    const response = await BrightMarketApi.getBrandAnalysisDetailCore(id, params);
    return response?.data ?? null;
  } catch (e) {
    console.log("Error fetching brand analysis detail core", e);
    return null;
  }
};

export const getBrandAnalysisDetailChart = async (
  id: number,
  params: { period?: number; channelId?: number; branchOfficeId?: number } = {}
): Promise<BrandAnalysisDetailChart | null> => {
  try {
    const response = await BrightMarketApi.getBrandAnalysisDetailChart(id, params);
    return response?.data ?? null;
  } catch (e) {
    console.log("Error fetching brand analysis detail chart", e);
    return null;
  }
};

export const getBrandAnalysisDetailBreakdown = async (
  id: number,
  params: { period?: number; channelId?: number; branchOfficeId?: number } = {}
): Promise<BrandAnalysisDetailBreakdown | null> => {
  try {
    const response = await BrightMarketApi.getBrandAnalysisDetailBreakdown(id, params);
    return response?.data ?? null;
  } catch (e) {
    console.log("Error fetching brand analysis detail breakdown", e);
    return null;
  }
};

export const getStoreAnalysisLanding = async (params: {
  search?: string;
  period?: number;
  sortBy?: string;
  sortOrder?: string;
}): Promise<StoreAnalysisLanding> => {
  try {
    const response = await BrightMarketApi.getStoreAnalysisLanding(params);
    return response?.data ?? { stores: [] };
  } catch (e) {
    console.log("Error fetching store analysis landing", e);
    return { stores: [] };
  }
};

export const getStoreAnalysisDetail = async (
  id: number,
  params: { period?: number } = {}
): Promise<StoreAnalysisDetail | null> => {
  try {
    const response = await BrightMarketApi.getStoreAnalysisDetail(id, params);
    return response?.data ?? null;
  } catch (e) {
    console.log("Error fetching store analysis detail", e);
    return null;
  }
};

export const getStoreAnalysisDetailCore = async (
  id: number,
  params: { period?: number } = {}
): Promise<StoreAnalysisDetailCore | null> => {
  try {
    const response = await BrightMarketApi.getStoreAnalysisDetailCore(id, params);
    return response?.data ?? null;
  } catch (e) {
    console.log("Error fetching store analysis detail core", e);
    return null;
  }
};

export const getStoreAnalysisDetailChart = async (
  id: number,
  params: { period?: number } = {}
): Promise<StoreAnalysisDetailChart | null> => {
  try {
    const response = await BrightMarketApi.getStoreAnalysisDetailChart(id, params);
    return response?.data ?? null;
  } catch (e) {
    console.log("Error fetching store analysis detail chart", e);
    return null;
  }
};

export const getStoreAnalysisDetailBreakdown = async (
  id: number,
  params: { period?: number } = {}
): Promise<StoreAnalysisDetailBreakdown | null> => {
  try {
    const response = await BrightMarketApi.getStoreAnalysisDetailBreakdown(
      id,
      params
    );
    return response?.data ?? null;
  } catch (e) {
    console.log("Error fetching store analysis detail breakdown", e);
    return null;
  }
};

export const getChannelAnalysisLanding = async (params: {
  search?: string;
  channelIds?: number[];
  period?: number;
  sortBy?: string;
  sortOrder?: string;
} = {}): Promise<ChannelAnalysisLanding> => {
  try {
    const response = await BrightMarketApi.getChannelAnalysisLanding(params);
    return response?.data ?? { channels: [], mix: [] };
  } catch (e) {
    console.log("Error fetching channel analysis landing", e);
    return { channels: [], mix: [] };
  }
};

export const getChannelAnalysisDetail = async (
  id: number,
  params: { period?: number; search?: string } = {}
): Promise<ChannelAnalysisDetail | null> => {
  try {
    const response = await BrightMarketApi.getChannelAnalysisDetail(id, params);
    return response?.data ?? null;
  } catch (e) {
    console.log("Error fetching channel analysis detail", e);
    return null;
  }
};

export const getChannelAnalysisDetailCore = async (
  id: number,
  params: { period?: number } = {}
): Promise<ChannelAnalysisDetailCore | null> => {
  try {
    const response = await BrightMarketApi.getChannelAnalysisDetailCore(id, params);
    return response?.data ?? null;
  } catch (e) {
    console.log("Error fetching channel analysis detail core", e);
    return null;
  }
};

export const getChannelAnalysisDetailChart = async (
  id: number,
  params: { period?: number } = {}
): Promise<ChannelAnalysisDetailChart | null> => {
  try {
    const response = await BrightMarketApi.getChannelAnalysisDetailChart(
      id,
      params
    );
    return response?.data ?? null;
  } catch (e) {
    console.log("Error fetching channel analysis detail chart", e);
    return null;
  }
};

export const getChannelAnalysisDetailBreakdown = async (
  id: number,
  params: { period?: number; search?: string } = {}
): Promise<ChannelAnalysisDetailBreakdown | null> => {
  try {
    const response = await BrightMarketApi.getChannelAnalysisDetailBreakdown(
      id,
      params
    );
    return response?.data ?? null;
  } catch (e) {
    console.log("Error fetching channel analysis detail breakdown", e);
    return null;
  }
};

// --- Category Analysis ---

export const getCategoryAnalysis = async (
  period: number,
  channelId?: number,
  branchOfficeId?: number
) => {
  try {
    const response = await BrightMarketApi.getCategoryAnalysis(period, channelId, branchOfficeId);
    return response.data;
  } catch (e) {
    console.log("Error fetching category analysis", e);
    return null;
  }
};

export const getCategoryAnalysisDetail = async (
  categoryId: number,
  period: number,
  channelId?: number,
  branchOfficeId?: number
): Promise<CategoryAnalysisDetail | null> => {
  try {
    const response = await BrightMarketApi.getCategoryAnalysisDetail(
      categoryId,
      period,
      channelId,
      branchOfficeId
    );
    return response?.data ?? null;
  } catch (e) {
    console.log("Error fetching category analysis detail", e);
    return null;
  }
};

export const getCategoryAnalysisDetailCore = async (
  categoryId: number,
  params: { period?: number; channelId?: number; branchOfficeId?: number } = {}
): Promise<CategoryAnalysisDetailCore | null> => {
  try {
    const response = await BrightMarketApi.getCategoryAnalysisDetailCore(categoryId, params);
    return response?.data ?? null;
  } catch (e) {
    console.log("Error fetching category analysis detail core", e);
    return null;
  }
};

export const getCategoryAnalysisDetailChart = async (
  categoryId: number,
  params: { period?: number; channelId?: number; branchOfficeId?: number } = {}
): Promise<CategoryAnalysisDetailChart | null> => {
  try {
    const response = await BrightMarketApi.getCategoryAnalysisDetailChart(categoryId, params);
    return response?.data ?? null;
  } catch (e) {
    console.log("Error fetching category analysis detail chart", e);
    return null;
  }
};

export const getCategoryAnalysisDetailBreakdown = async (
  categoryId: number,
  params: { period?: number; channelId?: number; branchOfficeId?: number } = {}
): Promise<CategoryAnalysisDetailBreakdown | null> => {
  try {
    const response = await BrightMarketApi.getCategoryAnalysisDetailBreakdown(categoryId, params);
    return response?.data ?? null;
  } catch (e) {
    console.log("Error fetching category analysis detail breakdown", e);
    return null;
  }
};


export const getClientAnalysis = async (
  period: number,
  channelId?: number,
  branchOfficeId?: number
) => {
  try {
    const response = await BrightMarketApi.getClientAnalysis(
      period,
      channelId,
      branchOfficeId
    );
    return response?.data ?? null;
  } catch (e) {
    console.log("Error fetching client analysis", e);
    return null;
  }
};

export const getActiveSeasons = async () => {
  try {
    const response = await BrightMarketApi.getActiveSeasons();
    return response?.data ?? null;
  } catch (e) {
    console.log("Error fetching active seasons", e);
    return null;
  }
};

export const getClientDiagnosis = async (clientId: number | string) => {
  try {
    const response = await BrightMarketApi.getClientDiagnosis(clientId);
    return response?.data ?? null;
  } catch (e) {
    console.log("Error fetching client diagnosis", e);
    return null;
  }
};

export const getCommercialIntelligenceDashboard = async () => {
  try {
    const response = await BrightMarketApi.getCommercialIntelligenceDashboard();
    return response?.data ?? null;
  } catch (e) {
    console.log("Error fetching commercial intelligence dashboard", e);
    return null;
  }
};

export const getCommercialIntelligenceFeaturedCampaign = async (period: string) => {
  try {
    const response =
      await BrightMarketApi.getCommercialIntelligenceFeaturedCampaign(period);
    return response?.data ?? null;
  } catch (e) {
    console.log("Error fetching featured campaign", e);
    return null;
  }
};

export const getProductMixAnalysis = async (
  startDate: string,
  endDate: string,
  channelId?: number,
  branchOfficeId?: number,
  categoryId?: number,
  brandId?: number,
  articleId?: number
) => {
  try {
    const response = await BrightMarketApi.getProductMixAnalysis(
      startDate,
      endDate,
      channelId,
      branchOfficeId,
      categoryId,
      brandId,
      articleId
    );
    return (response as { data?: import("../types/productMixAnalysis").ProductMixAnalysis })
      ?.data ?? response;
  } catch (e) {
    console.log("Error fetching product mix analysis", e);
    return null;
  }
};

export const getDailyControl = async () => {
  try {
    const response = await BrightMarketApi.getDailyControl();
    return (response as { data?: import("../types/dailyControl").DailyControlData })?.data ??
      null;
  } catch (e) {
    console.log("Error fetching daily control", e);
    return null;
  }
};

export const getDailyControlSummary = async () => {
  try {
    const response = await BrightMarketApi.getDailyControlSummary();
    return (response as { data?: import("../types/dailyControl").DailyControlSummaryData })?.data ?? null;
  } catch (e) {
    console.log("Error fetching daily control summary", e);
    return null;
  }
};

export const getDailyControlGaps = async () => {
  try {
    const response = await BrightMarketApi.getDailyControlGaps();
    return (response as { data?: import("../types/dailyControl").DailyControlGapsData })?.data ?? null;
  } catch (e) {
    console.log("Error fetching daily control gaps", e);
    return null;
  }
};

export type WholesaleFilterOption = { id: number; name: string };

export type WholesaleFiltersData = {
  categories: WholesaleFilterOption[];
  brands: WholesaleFilterOption[];
};

export const getCommercialSummaryCore = async (
  params: import("../types/commercialSummary").CommercialSummaryFilterParams
): Promise<import("../types/commercialSummary").CommercialSummaryCore | null> => {
  try {
    const response = await BrightMarketApi.getCommercialSummaryCore(
      params.startDate,
      params.endDate,
      params.channelId,
      params.branchOfficeId,
      params.categoryId,
      params.brandId
    );
    return response.data;
  } catch (e) {
    console.log("Error fetching commercial summary core", e);
    return null;
  }
};

export const getCommercialSummaryProducts = async (
  params: import("../types/commercialSummary").CommercialSummaryFilterParams
): Promise<import("../types/commercialSummary").CommercialSummaryProducts | null> => {
  try {
    const response = await BrightMarketApi.getCommercialSummaryProducts(
      params.startDate,
      params.endDate,
      params.channelId,
      params.branchOfficeId,
      params.categoryId,
      params.brandId
    );
    return response.data;
  } catch (e) {
    console.log("Error fetching commercial summary products", e);
    return null;
  }
};

export const getCommercialSummaryClients = async (
  params: import("../types/commercialSummary").CommercialSummaryFilterParams
): Promise<import("../types/commercialSummary").CommercialSummaryClients | null> => {
  try {
    const response = await BrightMarketApi.getCommercialSummaryClients(
      params.startDate,
      params.endDate,
      params.channelId,
      params.branchOfficeId,
      params.categoryId,
      params.brandId
    );
    return response.data;
  } catch (e) {
    console.log("Error fetching commercial summary clients", e);
    return null;
  }
};

export const getWholesaleFilters = async (): Promise<WholesaleFiltersData | null> => {
  try {
    const response = await BrightMarketApi.getWholesaleFilters();
    const data = (response as { data?: WholesaleFiltersData })?.data;
    return {
      categories: data?.categories ?? [],
      brands: data?.brands ?? [],
    };
  } catch (e) {
    console.log("Error fetching wholesale filters", e);
    return null;
  }
};

const unwrapCommercialDateAnalysisPayload = <T>(response: {
  data?: { data?: T } & T;
}): T | null => {
  const body = response?.data;
  if (!body) return null;
  if (body.data !== undefined && body.data !== null) {
    return body.data as T;
  }
  return body as T;
};

export const getCommercialDateAnalysis = async () => {
  try {
    const response = await BrightMarketApi.getCommercialDateAnalysis();
    return unwrapCommercialDateAnalysisPayload<
      import("../types/commercialDateAnalysis").CommercialDateAnalysisLanding
    >(response);
  } catch (e) {
    console.log("Error fetching commercial date analysis", e);
    return null;
  }
};

export const getCommercialDateAnalysisEvents = async () => {
  try {
    const response = await BrightMarketApi.getCommercialDateAnalysisEvents();
    return unwrapCommercialDateAnalysisPayload<{
      items: import("../types/commercialDateAnalysis").CommercialDateAnalysisEventMeta[];
    }>(response);
  } catch (e) {
    console.log("Error fetching commercial date analysis events", e);
    return null;
  }
};

export const getCommercialDateAnalysisAnnualTotals = async () => {
  try {
    const response = await BrightMarketApi.getCommercialDateAnalysisAnnualTotals();
    return unwrapCommercialDateAnalysisPayload<{
      totals: import("../types/commercialDateAnalysis").CommercialDateAnalysisAnnualTotal[];
    }>(response);
  } catch (e) {
    console.log("Error fetching commercial date analysis annual totals", e);
    return null;
  }
};

export const getCommercialDateAnalysisLandingMetrics = async (commercialDateId: number) => {
  try {
    const response = await BrightMarketApi.getCommercialDateAnalysisLandingMetrics(
      commercialDateId
    );
    return unwrapCommercialDateAnalysisPayload<
      import("../types/commercialDateAnalysis").CommercialDateAnalysisLandingMetrics
    >(response);
  } catch (e) {
    console.log("Error fetching commercial date landing metrics", e);
    return null;
  }
};

export const getWholesaleAnalysis = async (
  startDate: string,
  endDate: string,
  categoryId?: number,
  brandId?: number,
  wholesalerId?: number
) => {
  try {
    const response = await BrightMarketApi.getWholesaleAnalysis(
      startDate,
      endDate,
      categoryId,
      brandId,
      wholesalerId
    );
    return response.data;
  } catch (e) {
    console.log("Error fetching wholesale analysis", e);
    return null;
  }
};

export type WholesalerReferenceOption = {
  id: number;
  label: string;
  secondary?: string;
};

export type WholesalerReferenceData = {
  branchOffices: WholesalerReferenceOption[];
  salePoints: WholesalerReferenceOption[];
  clients: WholesalerReferenceOption[];
};

export const getWholesaleReferenceData = async (): Promise<WholesalerReferenceData | null> => {
  try {
    const response = await BrightMarketApi.getWholesaleReferenceData();
    return (response as { data?: WholesalerReferenceData })?.data ?? null;
  } catch (e) {
    console.log("Error fetching wholesale reference data", e);
    return null;
  }
};

export const getWholesalers = async () => {
  try {
    const response = await BrightMarketApi.getWholesalers();
    return (response as { data?: unknown[] })?.data ?? [];
  } catch (e) {
    console.log("Error fetching wholesalers", e);
    return [];
  }
};

export const getWholesaleSettings = async () => {
  try {
    const response = await BrightMarketApi.getWholesaleSettings();
    return (response as { data?: { identificationModes?: string[] } })?.data ?? null;
  } catch (e) {
    console.log("Error fetching wholesale settings", e);
    return null;
  }
};

export const saveWholesaleSettings = async (identificationModes: string[]) => {
  const response = await BrightMarketApi.saveWholesaleSettings(identificationModes);
  return (response as { data?: { identificationModes?: string[] } })?.data ?? null;
};

export const createWholesaler = async (body: Record<string, unknown>) => {
  const response = await BrightMarketApi.createWholesaler(body);
  return (response as { data?: unknown })?.data;
};

export const updateWholesaler = async (id: number, body: Record<string, unknown>) => {
  const response = await BrightMarketApi.updateWholesaler(id, body);
  return (response as { data?: unknown })?.data;
};

export const deleteWholesaler = async (id: number) => {
  await BrightMarketApi.deleteWholesaler(id);
};
export const getCommercialDateAnalysisDetail = async (
  commercialDateId: number,
  year?: number
) => {
  try {
    const response = await BrightMarketApi.getCommercialDateAnalysisDetail(
      commercialDateId,
      year
    );
    return unwrapCommercialDateAnalysisPayload<
      import("../types/commercialDateAnalysis").CommercialDateAnalysisDetail
    >(response);
  } catch (e) {
    console.log("Error fetching commercial date analysis detail", e);
    return null;
  }
};

export type FaissIndexFile = {
  fileId: string;
  originalFilename: string;
  size: number;
  contentType?: string;
  createdAt?: string;
  active: boolean;
  organizationId?: number;
};

export type ProductSimilaritySettings = {
  organizationId: number;
  topK: number;
  skipDuplicates: boolean;
  maxDistance: number;
  sameCategory: boolean;
  sameBrand: boolean;
  maxPriceDifferencePerc: number;
};

export const listFaissIndexes = async (
  organizationId: number,
  page = 0,
  size = 20
) => {
  try {
    const response = await BrightMarketApi.listFaissIndexes(
      organizationId,
      page,
      size
    );
    const pageData = response?.data;
    return {
      items: (pageData?.content ?? []) as FaissIndexFile[],
      totalElements: pageData?.totalElements ?? 0,
    };
  } catch (e) {
    console.log("Error listing FAISS indexes", e);
    throw e;
  }
};

export const getActiveFaissIndex = async (organizationId: number) => {
  try {
    const response = await BrightMarketApi.getActiveFaissIndex(organizationId);
    return (response?.data ?? null) as FaissIndexFile | null;
  } catch (e) {
    console.log("Error fetching active FAISS index", e);
    return null;
  }
};

export const setActiveFaissIndex = async (
  organizationId: number,
  fileId: string | null
) => {
  const response = await BrightMarketApi.setActiveFaissIndex(
    organizationId,
    fileId
  );
  return (response?.data ?? null) as FaissIndexFile | null;
};

export const getProductSimilaritySettings = async (
  organizationId: number
): Promise<ProductSimilaritySettings> => {
  const response = await BrightMarketApi.getProductSimilaritySettings(
    organizationId
  );
  return response?.data as ProductSimilaritySettings;
};

export const updateProductSimilaritySettings = async (
  settings: ProductSimilaritySettings
): Promise<ProductSimilaritySettings> => {
  const response = await BrightMarketApi.updateProductSimilaritySettings(
    settings
  );
  return response?.data as ProductSimilaritySettings;
};

export const uploadFaissIndex = async (
  organizationId: number,
  file: File,
  onUploadProgress?: (progress: {
    loaded: number;
    total?: number;
    percent: number;
  }) => void
) => {
  return BrightMarketApi.uploadFaissIndex(
    organizationId,
    file,
    onUploadProgress
  );
};

export const deleteFaissIndex = async (fileId: string) => {
  return BrightMarketApi.deleteFaissIndex(fileId);
};

export const downloadFaissIndex = async (
  fileId: string,
  filename?: string
) => {
  const response = await BrightMarketApi.downloadFaissIndex(fileId);
  const blob = new Blob([response.data]);
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename || `faiss_${fileId}.index`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export type DemandForecastArtifactKind = "pipeline" | "model" | "config";

export type DemandForecastArtifactFile = {
  fileId: string;
  originalFilename: string;
  size: number;
  contentType?: string;
  createdAt?: string;
  active: boolean;
  organizationId?: number;
  kind?: DemandForecastArtifactKind;
};

export const listDemandForecastArtifacts = async (
  organizationId: number,
  kind: DemandForecastArtifactKind,
  page = 0,
  size = 10
) => {
  const response = await BrightMarketApi.listDemandForecastArtifacts(
    organizationId,
    kind,
    page,
    size
  );
  const pageData = response?.data;
  return {
    items: (pageData?.content ?? []) as DemandForecastArtifactFile[],
    totalElements: pageData?.totalElements ?? 0,
  };
};

export const getDemandForecastArtifactSelection = async (organizationId: number) => {
  const response = await BrightMarketApi.getDemandForecastArtifactSelection(organizationId);
  return (response?.data ?? {}) as Partial<
    Record<DemandForecastArtifactKind, DemandForecastArtifactFile | null>
  >;
};

export const uploadDemandForecastArtifact = async (
  organizationId: number,
  kind: DemandForecastArtifactKind,
  file: File
) => {
  return BrightMarketApi.uploadDemandForecastArtifact(
    organizationId,
    kind,
    file
  );
};

export const setActiveDemandForecastArtifact = async (
  organizationId: number,
  kind: DemandForecastArtifactKind,
  fileId: string | null
) => {
  const response = await BrightMarketApi.setActiveDemandForecastArtifact(
    organizationId,
    kind,
    fileId
  );
  return (response?.data ?? null) as DemandForecastArtifactFile | null;
};

export const renameDemandForecastArtifact = async (
  fileId: string,
  originalFilename: string
) => {
  const response = await BrightMarketApi.renameDemandForecastArtifact(
    fileId,
    originalFilename
  );
  return response?.data as DemandForecastArtifactFile;
};

export const deleteDemandForecastArtifact = async (fileId: string) => {
  return BrightMarketApi.deleteDemandForecastArtifact(fileId);
};

export const downloadDemandForecastArtifact = async (
  fileId: string,
  filename?: string
) => {
  const response = await BrightMarketApi.downloadDemandForecastArtifact(fileId);
  const blob = new Blob([response.data]);
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename || `forecast_${fileId}`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};
  
